const R2_ARTWORK_FILE_LIMIT_BYTES = 50 * 1024 * 1024;
const R2_ARTWORK_TOTAL_LIMIT_BYTES = 500 * 1024 * 1024;
const ARTWORK_FILE_HELP = 'PDF, AI, EPS, PNG, JPG, JPEG, TIF, TIFF or SVG · Maximum 50 MB per file';
const SHEET_SAFE_MARGIN_INCHES = 8 / 25.4;
const SHEET_LABEL_GAP_INCHES = 2 / 25.4;
const LEGACY_PRICING_SHEET_GAP_INCHES = 0.1;
const DEFAULT_LABELS_PER_SHEET = 15;
// Configurator price tables and price-point variants are priced in the shop's
// base currency. Other markets (for example USD with duties, taxes and fees
// included) must display Shopify's own presentment price for the exact variant
// that checkout will charge, never the base-currency number.
const SHOP_BASE_CURRENCY = 'CAD';
const PRESENTMENT_PRICE_PENDING = '…';
const PRESENTMENT_PRICE_UNAVAILABLE = 'Price shown in cart';

class SparkProductConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.productionBenefitsMarkup === undefined) {
      this.productionBenefitsMarkup = this.querySelector('[data-configurator-production-benefits]')?.innerHTML.trim() || '';
      this.checkoutBenefitsMarkup = this.querySelector('[data-configurator-checkout-benefits]')?.innerHTML.trim() || '';
    }
    this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');
    this.configUrl = this.dataset.configUrl || '';
    this.allowApiFallback = this.dataset.allowApiFallback === 'true';
    this.productHandle = this.dataset.productHandle || '';
    this.productTitle = this.dataset.productTitle || 'Custom product';
    this.currency = this.dataset.currency || 'CAD';
    this.country = String(this.dataset.country || '').toUpperCase();
    this.presentmentPrices = new Map();
    this.presentmentPriceRequests = new Map();
    this.profile = null;
    this.localMatrix = null;
    this.currentPriceRow = null;
    this.state = {};
    this.price = null;
    this.lastRenderedPriceTotal = null;
    this.loading = false;
    this.step = 'options';
    this.statusMessage = '';
    this.statusType = '';
    this.artworkFileSelectionKey = '';
    this.artworkFiles = [];
    this.artworkPreviewUrls = new Map();
    this.artworkManifestToken = '';
    this.artworkAccessUrl = '';
    this.artworkManifestSelectionKey = '';
    this.artworkUploadProgress = 0;
    this.uploadApiBase = this.apiBase || 'https://configurator.sparkstickers.com';
    this.showAllFixedQuantities = false;
    this.versionsDropdownOpen = false;
    this.preparedVariantCache = new Map();
    this.preparedVariantTimer = null;
    this.activePreparedVariantKey = '';
    this.stickyObserver = null;
    this.stickyScrollController = null;
    this.stickyReachedOptions = false;
    this.handleDocumentPointerDown = this.handleDocumentPointerDown || ((event) => {
      const dropdown = this.querySelector('.spark-config__versions-dropdown[open]');
      if (dropdown && !dropdown.contains(event.target)) {
        this.versionsDropdownOpen = false;
        dropdown.removeAttribute('open');
      }
    });
    document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
    document.addEventListener('pointerdown', this.handleDocumentPointerDown);
    this.restoreTopScrollOnReload();
    this.load();
  }

  disconnectedCallback() {
    if (this.stickyObserver) {
      this.stickyObserver.disconnect();
      this.stickyObserver = null;
    }
    if (this.stickyScrollController) {
      this.stickyScrollController.abort();
      this.stickyScrollController = null;
    }
    this.clearPreparedVariantTimer();
    this.cancelPreparedVariantRequestsExcept('');
    this.clearArtworkPreviewUrls();
    document.removeEventListener('pointerdown', this.handleDocumentPointerDown);
  }

  restoreTopScrollOnReload() {
    try {
      const navEntry = performance.getEntriesByType?.('navigation')?.[0];
      const previousScrollY = Number(sessionStorage.getItem('spark-product-scroll-y') || 0);
      const shouldForceTop = navEntry?.type === 'reload' && previousScrollY <= 24 && !window.location.hash;

      if (shouldForceTop) {
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        const forceTop = () => window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        [0, 80, 240, 520, 900, 1400, 2200].forEach((delay) => window.setTimeout(forceTop, delay));
        window.addEventListener('load', () => {
          [0, 120, 420].forEach((delay) => window.setTimeout(forceTop, delay));
        }, { once: true });
      }

      window.addEventListener('pagehide', () => {
        sessionStorage.setItem('spark-product-scroll-y', String(Math.max(0, Math.round(window.scrollY || 0))));
      });
    } catch (error) {
      // Scroll restoration is a progressive enhancement only.
    }
  }

  async load() {
    if (!this.productHandle) {
      this.renderShell('Configurator is not connected for this product.');
      return;
    }

    this.renderShell('Loading configurator...');

    try {
      const payload = await this.loadConfiguratorPayload();
      this.localMatrix = payload.localMatrix || null;
      this.profile = payload.profile;
      this.state = this.restoreConfigurationState(this.defaultState(this.profile));
      this.normalizeVisibleShapeSelection();
      if (this.profile.engine === 'sheets') {
        this.state.finish = this.normalizedSheetFinish(this.state.finish);
      }
      this.render();
      await this.updatePrice();
    } catch (error) {
      this.renderShell(error.message || 'Configurator is not available.');
    }
  }

  async loadConfiguratorPayload() {
    if (this.configUrl) {
      const response = await fetch(this.configUrl, { headers: { Accept: 'application/json' } });
      if (response.ok) {
        const matrix = await response.json();
        if (matrix?.profile) {
          normalizePricingMatrix(matrix);
          return { profile: matrix.profile, localMatrix: matrix };
        }
      }
    }

    if (this.apiBase && this.allowApiFallback) {
      const response = await fetch(`${this.apiBase}/api/configurator/profile/${encodeURIComponent(this.productHandle)}`, {
        headers: { Accept: 'application/json', 'bypass-tunnel-reminder': 'true' },
      });
      if (!response.ok) throw new Error('Configurator profile is not available.');
      const payload = await response.json();
      return { profile: payload.profile, localMatrix: null };
    }

    throw new Error('Local pricing data is not published for this product yet.');
  }

  defaultState(profile) {
    const engine = profile.engine;
    const state = {
      shape: profile.lockedShape ? (profile.defaultShape || 'diecut') : 'diecut',
      material: profile.defaultMaterial || '',
      finish: profile.defaultFinish || 'none',
      lamination: 'none',
      unwindDirection: 'Right of Copy',
      artworkMode: 'upload',
      artworkNotes: '',
      artworkReminderEmail: '',
      customSize: false,
      customQuantity: false,
      customArtworkVersions: false,
    };

    if (engine === 'stickers' || engine === 'sheets') {
      const defaultSize = engine === 'stickers' && ['rectangle', 'oval'].includes(state.shape)
        ? '2" x 1"'
        : '2" x 2"';
      const parsedDefaultSize = parseSize(defaultSize);
      Object.assign(state, {
        material: engine === 'sheets' ? (profile.defaultMaterial || 'paper') : (profile.defaultMaterial || 'vinylmatte'),
        width: parsedDefaultSize.width,
        height: parsedDefaultSize.height,
        size: defaultSize,
        sizeLabel: defaultSize,
        quantity: 100,
        labelQuantity: engine === 'sheets' ? 100 : undefined,
        artworkVersions: 1,
      });
    }

    if (engine === 'vista-roll-labels') {
      const model = profile.settings?.rollLabels || {};
      const rollShapeValues = this.orderedRollShapeValues(model.shapes);
      const shape = rollShapeValues[0] || profile.defaultShape || 'Circle';
      const compatibleSizes = model.compatible_sizes_by_shape?.[shape] || model.fixed_sizes || [];
      const material = profile.defaultMaterial || profile.materialOrder?.[0] || model.materials?.[0] || 'White Paper';
      const compatibleFinishes = this.rollFinishes(material);
      Object.assign(state, {
        shape,
        size: this.preferredTwoByTwoValue(compatibleSizes),
        material,
        finish: compatibleFinishes[0]?.value || profile.defaultFinish || model.finishes?.[0] || 'Gloss',
        quantity: model.quantities?.includes(100) ? 100 : model.quantities?.[0] || 50,
      });
    }

    if (engine === 'vista-magnets') {
      const model = profile.settings?.magnets || {};
      const shape = profile.defaultShape || model.shapes?.[0] || 'Circle';
      const compatibleSizes = model.compatible_sizes_by_shape?.[shape] || model.sizes || [];
      const compatibleOrientations = model.compatible_orientations_by_shape?.[shape] || model.orientations || [];
      Object.assign(state, {
        shape,
        size: this.preferredTwoByTwoValue(compatibleSizes) || '2" x 2"',
        orientation: compatibleOrientations[0] || 'Vertical',
        finish: profile.defaultFinish || model.finishes?.[0] || 'None',
        quantity: model.quantities?.includes(100) ? 100 : model.quantities?.[0] || 10,
      });
    }

    if (engine === 'jukebox-magnets') {
      const model = profile.settings?.magnets || {};
      const shape = profile.defaultShape || model.shapes?.[0] || 'diecut';
      const compatibleSizes = model.compatible_sizes_by_shape?.[shape] || [];
      const size = this.preferredTwoByTwoValue(compatibleSizes) || compatibleSizes[0] || '2" x 2"';
      const parsedSize = parseSize(size);
      Object.assign(state, {
        shape,
        size,
        sizeLabel: size,
        width: parsedSize.width,
        height: parsedSize.height,
        quantity: model.quantities?.includes(100) ? 100 : model.quantities?.[0] || 50,
      });
    }

    return state;
  }

  configurationSnapshot() {
    const allowedKeys = [
      'shape', 'material', 'finish', 'lamination', 'unwindDirection', 'width', 'height', 'size', 'sizeLabel',
      'quantity', 'labelQuantity', 'orientation', 'artworkVersions',
      'customSize', 'customQuantity', 'customArtworkVersions', 'artworkMode', 'artworkNotes', 'artworkReminderEmail',
    ];
    const state = {};
    allowedKeys.forEach((key) => {
      const value = this.state[key];
      if (value !== undefined && value !== null && value !== '') state[key] = value;
    });
    return { version: 1, productHandle: this.productHandle, state };
  }

  configurationToken() {
    const bytes = new TextEncoder().encode(JSON.stringify(this.configurationSnapshot()));
    let binary = '';
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  parseConfigurationSnapshot(value) {
    if (!value) return null;
    if (value.startsWith('{')) return JSON.parse(value);
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const binary = window.atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  }

  restoreConfigurationState(defaultState) {
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get('variant') || '';
    const encodedConfiguration = params.get('spark_config');
    const restoreArtworkPreference = Boolean(encodedConfiguration);
    let snapshot = null;

    try {
      if (encodedConfiguration) snapshot = this.parseConfigurationSnapshot(encodedConfiguration);
      if (!snapshot && variantId) {
        snapshot = JSON.parse(window.localStorage.getItem(this.configurationStorageKey(variantId)) || 'null');
      }
      if (!snapshot && variantId) {
        snapshot = JSON.parse(window.localStorage.getItem(this.configurationStorageKey('latest')) || 'null');
      }
    } catch (_error) {
      snapshot = null;
    }

    if (!snapshot || snapshot.productHandle !== this.productHandle || !snapshot.state || typeof snapshot.state !== 'object') {
      return defaultState;
    }

    const allowedKeys = new Set([
      'shape', 'material', 'finish', 'lamination', 'unwindDirection', 'width', 'height', 'size', 'sizeLabel',
      'quantity', 'labelQuantity', 'orientation', 'artworkVersions',
      'customSize', 'customQuantity', 'customArtworkVersions', 'artworkMode', 'artworkNotes', 'artworkReminderEmail',
    ]);
    const restored = { ...defaultState };
    Object.entries(snapshot.state).forEach(([key, value]) => {
      if (!allowedKeys.has(key)) return;
      // A normal product visit should always begin with the uploader selected.
      // Only an explicit cart-edit configuration may restore "email later".
      if (key === 'artworkMode' && !restoreArtworkPreference) return;
      if (!['string', 'number', 'boolean'].includes(typeof value)) return;
      restored[key] = value;
    });
    return restored;
  }

  configurationStorageKey(variantId) {
    const schemaVersions = {
      stickers: 'v3:',
      sheets: 'v2:',
      'vista-roll-labels': 'v2:',
      'vista-magnets': 'v2:',
      'jukebox-magnets': 'v1:',
    };
    const schemaVersion = schemaVersions[this.profile?.engine] || '';
    return `spark-config:${schemaVersion}${this.productHandle}:${variantId}`;
  }

  persistConfigurationSnapshot(variantId) {
    try {
      const serialized = JSON.stringify(this.configurationSnapshot());
      window.localStorage.setItem(this.configurationStorageKey(String(variantId)), serialized);
      window.localStorage.setItem(this.configurationStorageKey('latest'), serialized);
    } catch (_error) {
      // Cart links still carry the exact snapshot when local storage is unavailable.
    }
  }

  renderShell(message) {
    this.innerHTML = `
      <div class="spark-config">
        <div class="spark-config__empty">
          <strong>Choose options for instant pricing</strong>
          <span>${escapeHtml(message)}</span>
        </div>
      </div>
    `;
  }

  renderFlowHeader(isArtworkStep) {
    const optionsStep = isArtworkStep
      ? `
        <button class="spark-config__flow-step is-complete" type="button" data-back-to-options aria-label="Return to product options">
          <span class="spark-config__flow-number" aria-hidden="true">1</span>
          <span>
            <strong>Choose options</strong>
            <small>Size, finish, quantity</small>
          </span>
        </button>
      `
      : `
        <div class="spark-config__flow-step is-active" aria-current="step">
          <span class="spark-config__flow-number" aria-hidden="true">1</span>
          <span>
            <strong>Choose options</strong>
            <small>Size, finish, quantity</small>
          </span>
        </div>
      `;

    return `
      <div class="spark-config__flow" aria-label="Order progress">
        ${optionsStep}
        <div class="spark-config__flow-step${isArtworkStep ? ' is-active' : ''}" aria-current="${isArtworkStep ? 'step' : 'false'}">
          <span class="spark-config__flow-number" aria-hidden="true">2</span>
          <span>
            <strong>Upload artwork</strong>
            <small>Then add to cart</small>
          </span>
        </div>
      </div>
    `;
  }

  defaultStatusMarkup(isArtworkStep = this.step === 'artwork') {
    return '';
  }

  render() {
    const stickyWasVisible = this.querySelector('[data-sticky-actions]')?.classList.contains('is-visible') === true;
    const profile = this.profile;
    const sections = [];

    if (profile.engine === 'stickers' || profile.engine === 'sheets') {
      if (!profile.lockedShape) sections.push(this.renderShapeSection());
      sections.push(this.renderSizeSection());
      if (profile.engine === 'sheets') sections.push(this.renderSheetInfo());
      if (!profile.lockedMaterial) sections.push(this.renderMaterialSection());
      if (profile.engine === 'sheets' && !profile.lockedFinish) sections.push(this.renderFinishSection());
      if (profile.engine === 'stickers') sections.push(this.renderLaminationSection());
      if (profile.engine === 'sheets') sections.push(this.renderArtworkVersionsSection());
      sections.push(this.renderQuantitySection(profile.engine === 'sheets' ? 'Labels quantity' : 'Quantity'));
    }

    if (profile.engine === 'vista-roll-labels') {
      sections.push(this.renderChoiceSection('Shape', 'shape', this.rollShapes()));
      sections.push(this.renderSizeChoiceSection('Size', this.rollSizes()));
      if (!profile.lockedMaterial) sections.push(this.renderMaterialSection());
      sections.push(this.renderRollFinishSection());
      sections.push(this.renderRollDirectionSection());
      sections.push(this.renderQuantitySection('Quantity'));
    }

    if (profile.engine === 'vista-magnets') {
      sections.push(this.renderChoiceSection('Shape', 'shape', this.magnetShapes()));
      sections.push(this.renderChoiceSection('Size', 'size', this.magnetSizes()));
      sections.push(this.renderChoiceSection('Finish', 'finish', this.magnetFinishes()));
      sections.push(this.renderQuantitySection('Quantity'));
    }

    if (profile.engine === 'jukebox-magnets') {
      sections.push(this.renderShapeSection());
      sections.push(this.renderSizeSection());
      sections.push(this.renderQuantitySection('Quantity'));
    }

    const isArtworkStep = this.step === 'artwork';
    const visibleSections = isArtworkStep ? [this.renderArtworkSection()] : sections;
    const canContinueOptions = isArtworkStep || this.canContinueOptions();
    const canAddConfiguredItem = !isArtworkStep || this.canSubmitArtwork();
    const priceBlockMarkup = isArtworkStep
      ? ''
      : this.renderPriceBlock(this.consumePriceAnimationState());

    this.innerHTML = `
      <div class="spark-config spark-config--${isArtworkStep ? 'artwork' : 'options'}">
        ${this.renderFlowHeader(isArtworkStep)}
        <div class="spark-config__body">
          ${visibleSections.join('')}
        </div>
        <div class="spark-config__actions">
          ${isArtworkStep ? this.renderArtworkTotal() : priceBlockMarkup}
          ${this.productionBenefitsMarkup}
          ${isArtworkStep ? `
            <div class="spark-config__action-row">
              <button class="spark-config__add" type="button" data-add-to-cart aria-busy="${this.loading}" ${(!canAddConfiguredItem || this.loading) ? 'disabled aria-disabled="true"' : ''}>
                <span class="spark-config__spinner" aria-hidden="true"></span>
                <span data-add-to-cart-label>${escapeHtml(this.addToCartLabel())}</span>
                <svg class="spark-config__cart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" aria-hidden="true"><path d="M3 4h2l2.1 10.2a2 2 0 0 0 2 1.6h7.8a2 2 0 0 0 2-1.6L20 7H6"/><circle cx="10" cy="19" r="1.2"/><circle cx="17" cy="19" r="1.2"/></svg>
              </button>
            </div>
          ` : `
            <button class="spark-config__add" type="button" data-next-step ${canContinueOptions ? '' : 'disabled aria-disabled="true"'}>
              <span>Continue to upload artwork</span>
              <span aria-hidden="true">→</span>
            </button>
          `}
          <p class="spark-config__status${this.statusType ? ` is-${this.statusType}` : ''}" data-status role="status" aria-live="polite">
            ${this.statusMessage ? escapeHtml(this.statusMessage) : this.defaultStatusMarkup(isArtworkStep)}
          </p>
          ${this.checkoutBenefitsMarkup}
        </div>
        ${!isArtworkStep ? `
          <div class="spark-config__sticky-actions${stickyWasVisible ? ' is-visible' : ''}" data-sticky-actions aria-hidden="${stickyWasVisible ? 'false' : 'true'}">
            ${priceBlockMarkup}
            <button class="spark-config__add" type="button" data-next-step ${canContinueOptions ? '' : 'disabled aria-disabled="true"'}>
              <span>Continue to upload artwork</span>
              <span aria-hidden="true">→</span>
            </button>
          </div>
        ` : ''}
      </div>
    `;

    this.bindEvents();
    this.setupStickyActions();
    if (isArtworkStep) this.updateArtworkFileState();
  }

  bindEvents() {
    if (!this.inputFocusHandlersBound) {
      this.inputFocusHandlersBound = true;
      const activateIfTextInput = (event) => {
        if (this.contains(event.target) && isTextEntryControl(event.target)) {
          this.setInputActive(true);
        }
      };
      const releaseIfNoTextInput = () => {
        window.setTimeout(() => {
          this.setInputActive(this.isTextInputFocused());
        }, 80);
      };

      this.addEventListener('focusin', activateIfTextInput);
      this.addEventListener('focusout', releaseIfNoTextInput);
      document.addEventListener('focusin', activateIfTextInput, true);
      document.addEventListener('focusout', releaseIfNoTextInput, true);
    }

    this.bindOptionEvents(this);

    const helpOpen = this.querySelector('[data-lamination-help-open]');
    const helpDialog = this.querySelector('[data-lamination-help-dialog]');
    const helpClose = this.querySelector('[data-lamination-help-close]');
    if (helpOpen && helpDialog) {
      helpOpen.addEventListener('click', () => helpDialog.showModal());
      helpClose?.addEventListener('click', () => helpDialog.close());
      helpDialog.addEventListener('click', (event) => {
        if (event.target === helpDialog) helpDialog.close();
      });
    }

    const versionsHelpOpen = this.querySelector('[data-versions-help-open]');
    const versionsHelpDialog = this.querySelector('[data-versions-help-dialog]');
    const versionsHelpClose = this.querySelector('[data-versions-help-close]');
    if (versionsHelpOpen && versionsHelpDialog) {
      versionsHelpOpen.addEventListener('click', () => versionsHelpDialog.showModal());
      versionsHelpClose?.addEventListener('click', () => versionsHelpDialog.close());
      versionsHelpDialog.addEventListener('click', (event) => {
        if (event.target === versionsHelpDialog) versionsHelpDialog.close();
      });
    }

    const versionsDropdown = this.querySelector('.spark-config__versions-dropdown');
    versionsDropdown?.addEventListener('toggle', () => {
      this.versionsDropdownOpen = versionsDropdown.open;
    });

    this.querySelectorAll('[data-quantity-disclosure]').forEach((button) => {
      button.addEventListener('click', () => {
        this.showAllFixedQuantities = !this.showAllFixedQuantities;
        this.render();
      });
    });

    this.querySelectorAll('[data-artwork-mode]').forEach((button) => {
      button.addEventListener('click', () => {
        const willEmailArtwork = button.dataset.artworkMode === 'email_later';
        const modeInput = this.querySelector('[data-input="artworkMode"]');
        if (modeInput) modeInput.checked = willEmailArtwork;
        this.setField('artworkMode', willEmailArtwork, true);
      });
    });

    const fileInput = this.querySelector('[data-artwork-file]');
    if (fileInput) {
      fileInput.addEventListener('change', () => {
        const selectedFiles = Array.from(fileInput.files || []);
        if (selectedFiles.length) {
          this.mergeArtworkFiles(selectedFiles);
          this.invalidateArtworkManifest();
          fileInput.value = '';
          this.state.artworkMode = 'upload';
          const emailCheckbox = this.querySelector('[data-input="artworkMode"]');
          if (emailCheckbox) emailCheckbox.checked = false;
          this.statusMessage = '';
          this.statusType = '';
        }
        this.updateArtworkFileState();
        this.updateAddButtonState();
      });
    }

    const dropzone = this.querySelector('[data-artwork-dropzone]');
    if (dropzone && fileInput) {
      ['dragenter', 'dragover'].forEach((eventName) => {
        dropzone.addEventListener(eventName, (event) => {
          event.preventDefault();
          dropzone.classList.add('is-dragging');
        });
      });

      ['dragleave', 'drop'].forEach((eventName) => {
        dropzone.addEventListener(eventName, () => {
          dropzone.classList.remove('is-dragging');
        });
      });

      dropzone.addEventListener('drop', (event) => {
        event.preventDefault();
        const files = event.dataTransfer?.files;
        if (!files?.length) return;
        this.mergeArtworkFiles(Array.from(files));
        this.invalidateArtworkManifest();
        fileInput.value = '';
        this.state.artworkMode = 'upload';
        const emailCheckbox = this.querySelector('[data-input="artworkMode"]');
        if (emailCheckbox) emailCheckbox.checked = false;
        this.statusMessage = '';
        this.statusType = '';
        this.updateArtworkFileState();
        this.updateAddButtonState();
      });
    }

    const artworkFileList = this.querySelector('[data-artwork-file-list]');
    if (artworkFileList) {
      artworkFileList.addEventListener('click', (event) => {
        const removeButton = event.target.closest('[data-remove-artwork-file]');
        if (!removeButton || !artworkFileList.contains(removeButton)) return;
        const index = Number(removeButton.dataset.removeArtworkFile);
        if (!Number.isInteger(index) || index < 0) return;
        const [removedFile] = this.artworkFiles.splice(index, 1);
        this.releaseArtworkPreviewUrl(removedFile);
        this.invalidateArtworkManifest();
        if (fileInput) fileInput.value = '';
        this.artworkFileSelectionKey = '';
        this.updateArtworkFileState();
      });
    }

    const addButton = this.querySelector('[data-add-to-cart]');
    if (addButton) addButton.addEventListener('click', () => this.addToCart());

    this.querySelectorAll('[data-next-step]').forEach((nextButton) => nextButton.addEventListener('click', () => {
      if (!this.canContinueOptions()) {
        this.statusMessage = this.continueBlockMessage();
        this.statusType = 'error';
        this.patchStatusUi();
        this.patchCustomSizeMessage();
        this.patchContinueButtons();
        return;
      }
      this.step = 'artwork';
      this.statusMessage = '';
      this.statusType = '';
      this.render();
      this.focusArtworkStep(90, 'page');
    }));

    this.querySelectorAll('[data-back-to-options]').forEach((backButton) => backButton.addEventListener('click', () => {
      this.step = 'options';
      this.render();
      this.focusArtworkStep(0, 'page');
    }));
  }

  bindOptionEvents(scope) {
    this.querySelectorAll('[data-choice]').forEach((button) => {
      if (!scope.contains(button)) return;
      button.addEventListener('click', () => {
        const field = button.dataset.field;
        const value = button.dataset.value;
        this.setField(field, value);
      });
    });

    this.querySelectorAll('[data-input]').forEach((input) => {
      if (!scope.contains(input)) return;
      const eventName = input.type === 'checkbox' ? 'change' : 'input';
      input.addEventListener(eventName, () => {
        const field = input.dataset.input;
        const value = input.type === 'checkbox' ? input.checked : input.value;
        if (input.hasAttribute('data-custom-quantity-input')) {
          this.state.customQuantity = true;
        }
        this.setField(field, value, true);
      });
    });
  }

  setField(field, value, defer = false) {
    let shouldRender = true;
    let affectsPrice = true;
    let forceInlinePricePatch = false;

    if (field === 'shape') {
      this.state.shape = value;
      if (this.state.customSize) {
        this.applyCustomSizeLabels();
      } else {
        this.pickCompatibleSize();
      }
    } else if (field === 'size') {
      if (value === 'Custom') {
        this.state.customSize = true;
        this.state.size = 'Custom';
        this.state.sizeLabel = 'Custom';
        this.state.width = 0;
        this.state.height = 0;
        this.price = null;
        this.currentPriceRow = null;
        this.statusMessage = '';
        this.statusType = '';
        affectsPrice = false;
      } else {
        this.state.customSize = false;
        this.applySizeValue(value);
      }
    } else if (field === 'customWidth' || field === 'customHeight') {
      this.state[field === 'customWidth' ? 'width' : 'height'] = toNumber(value);
      this.applyCustomSizeLabels();
      shouldRender = false;
      this.patchCustomSizeMessage();
      if (this.isCustomSizeBlockingPrice()) {
        this.price = null;
        this.currentPriceRow = null;
        this.statusMessage = '';
        this.statusType = '';
        this.patchInlinePricingUi();
        affectsPrice = false;
      }
    } else if (field === 'material') {
      this.state.material = value;
      if (this.profile.engine === 'sheets') {
        this.state.finish = this.normalizedSheetFinish(this.state.finish, value);
      }
      if (this.profile.engine === 'stickers' && !this.laminationTypeForMaterial(value)) {
        this.state.lamination = 'none';
      }
      if (this.profile.engine === 'vista-roll-labels') {
        const availableFinishes = this.rollFinishes();
        if (!availableFinishes.some((finish) => finish.value === this.state.finish)) {
          this.state.finish = availableFinishes[0]?.value || 'Gloss';
        }
      }
    } else if (field === 'quantity' || field === 'labelQuantity') {
      const editingCustomQuantity = defer && this.state.customQuantity;
      this.state[field] = toInteger(value);
      if (!editingCustomQuantity) this.state.customQuantity = false;
      if (editingCustomQuantity) this.clearPresetQuantitySelection();
      shouldRender = !editingCustomQuantity;
      if (editingCustomQuantity && this.state[field] <= 0) {
        this.price = null;
        this.currentPriceRow = null;
        this.statusMessage = '';
        this.statusType = '';
        affectsPrice = false;
      }
    } else if (field === 'customQuantity') {
      this.state.customQuantity = true;
      const quantityField = this.quantityField();
      if (quantityField) this.state[quantityField] = 0;
      this.price = null;
      this.currentPriceRow = null;
      this.statusMessage = '';
      this.statusType = '';
      affectsPrice = false;
    } else if (field === 'artworkVersions') {
      if (value === 'custom') {
        this.state.customArtworkVersions = true;
      } else {
        this.state.customArtworkVersions = false;
        this.state.artworkVersions = toInteger(value);
        this.versionsDropdownOpen = false;
      }
      shouldRender = false;
      forceInlinePricePatch = true;
      this.patchArtworkVersionsControl();
    } else if (field === 'customArtworkVersions') {
      this.state.artworkVersions = Math.max(1, toInteger(value));
      shouldRender = false;
      this.patchArtworkVersionsSummary();
    } else if (field === 'artworkMode') {
      const artworkCheckbox = this.querySelector('[data-input="artworkMode"]');
      const artworkCheckboxTop = artworkCheckbox?.getBoundingClientRect().top;
      const artworkScrollContainer = artworkCheckbox ? this.scrollContainerFor(artworkCheckbox) : null;
      const previousOverflowAnchor = artworkScrollContainer?.style.overflowAnchor || '';
      const maintainArtworkPosition = () => this.restoreViewportTop('[data-input="artworkMode"]', artworkCheckboxTop);
      if (artworkScrollContainer) {
        artworkScrollContainer.style.overflowAnchor = 'none';
        artworkScrollContainer.addEventListener('scroll', maintainArtworkPosition, { passive: true });
      }
      this.state.artworkMode = value ? 'email_later' : 'upload';
      shouldRender = false;
      affectsPrice = false;
      this.statusMessage = '';
      this.statusType = '';
      if (value) {
        this.clearArtworkFiles();
        this.invalidateArtworkManifest();
        const fileInput = this.querySelector('[data-artwork-file]');
        if (fileInput) fileInput.value = '';
        this.artworkFileSelectionKey = '';
      }
      window.setTimeout(() => {
        this.updateArtworkFileState();
        this.updateAddButtonState();
        this.restoreViewportTop('[data-input="artworkMode"]', artworkCheckboxTop);
        window.setTimeout(() => {
          if (artworkScrollContainer) {
            artworkScrollContainer.removeEventListener('scroll', maintainArtworkPosition);
            artworkScrollContainer.style.overflowAnchor = previousOverflowAnchor;
          }
        }, 350);
      }, 0);
    } else if (field === 'artworkNotes' || field === 'artworkReminderEmail') {
      this.state[field] = value;
      shouldRender = false;
      affectsPrice = false;
    } else {
      this.state[field] = value;
    }

    if (shouldRender) this.render();
    if (affectsPrice) {
      window.clearTimeout(this.priceTimer);
      this.priceTimer = window.setTimeout(() => this.updatePrice({ forceInlinePricePatch }), defer ? 250 : 0);
    }
  }

  restoreViewportTop(selector, previousTop) {
    if (!Number.isFinite(previousTop)) return;

    const restore = () => {
      const element = this.querySelector(selector);
      if (!element) return;
      const delta = element.getBoundingClientRect().top - previousTop;
      if (Math.abs(delta) > 0.5) {
        const scrollContainer = this.scrollContainerFor(element);
        if (scrollContainer) {
          scrollContainer.scrollTop += delta;
        } else {
          window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
        }
      }
    };

    window.requestAnimationFrame(() => {
      restore();
      window.requestAnimationFrame(restore);
    });
  }

  scrollContainerFor(element) {
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
      const overflowY = window.getComputedStyle(parent).overflowY;
      const canScroll = /(auto|scroll|overlay)/.test(overflowY) && parent.scrollHeight > parent.clientHeight;
      if (canScroll) return parent;
    }
    return null;
  }

  applySizeValue(value) {
    const parsed = parseSize(value);
    this.state.size = value;
    this.state.sizeLabel = value.includes(' x ') ? value : value.replace('x', ' x ');
    this.state.width = parsed.width;
    this.state.height = parsed.height;
  }

  pickCompatibleSize() {
    if (this.profile.engine === 'vista-roll-labels') {
      const sizes = this.rollSizes().map((option) => option.value);
      this.setSizeWithoutRender(this.preferredTwoByTwoValue(sizes));
      return;
    }
    if (this.profile.engine === 'vista-magnets') {
      const sizes = this.magnetSizes().map((option) => option.value);
      this.setSizeWithoutRender(this.preferredTwoByTwoValue(sizes));
      const orientations = this.magnetOrientations().map((option) => option.value);
      this.state.orientation = orientations[0] || this.state.orientation;
      return;
    }
    const sizes = this.stickerSizes().map((option) => option.value);
    this.setSizeWithoutRender(this.preferredTwoByTwoValue(sizes));
  }

  setSizeWithoutRender(value) {
    if (!value) return;
    this.state.customSize = false;
    this.applySizeValue(value);
  }

  preferredTwoByTwoValue(values = []) {
    if (!values?.length) return '';
    return values.find((value) => normalizeSizeKey(value) === '2x2') || values[0];
  }

  applyCustomSizeLabels() {
    const width = Number(this.state.width || 0);
    const height = Number(this.state.height || 0);
    this.state.size = width > 0 && height > 0 ? formatSizeLabel(width, height, false) : 'Custom';
    this.state.sizeLabel = width > 0 && height > 0 ? formatSizeLabel(width, height, true) : 'Custom';
  }

  customSizeLimit() {
    if (this.profile?.engine === 'stickers') {
      if (this.profile?.priceProfile === 'hang-tags') {
        return {
          type: 'box', minWidth: 0.5, minHeight: 0.5, maxWidth: 12, maxHeight: 12,
          minimumMessage: 'Minimum size is 0.50" x 0.50".', maximumMessage: 'Maximum size is 12" x 12".',
        };
      }
      return {
        type: 'box', minWidth: 0.5, minHeight: 0.5, maxWidth: 52, maxHeight: 52,
        minimumMessage: 'Minimum size is 0.50" x 0.50".', maximumMessage: 'Maximum size is 52" x 52".',
      };
    }
    if (this.profile?.engine === 'sheets') {
      return {
        type: 'rectangle', minWidth: 0.5, minHeight: 0.5, maxLongSide: 18, maxShortSide: 12,
        minimumMessage: 'Minimum size is 0.50" x 0.50".', maximumMessage: 'Maximum size is 12" x 18" or 18" x 12".',
      };
    }
    if (this.profile?.engine === 'vista-roll-labels') {
      return { type: 'box', maxWidth: 5.5, maxHeight: 5.5, maximumMessage: 'Maximum size is 5.5" x 5.5".' };
    }
    if (this.profile?.engine === 'jukebox-magnets') {
      return {
        type: 'box', minWidth: 1, minHeight: 1, maxWidth: 52, maxHeight: 72,
        minimumMessage: 'Minimum size is 1" x 1".', maximumMessage: 'Maximum size is 52" wide x 72" high.',
      };
    }
    return null;
  }

  customRollDimensions() {
    if (this.profile?.engine !== 'vista-roll-labels') return [];
    const dimensions = this.localMatrix?.customRollPriceIndex?.dimensions || [];
    return dimensions
      .map((dimension) => Number(dimension))
      .filter((dimension) => Number.isFinite(dimension) && dimension > 0)
      .sort((first, second) => first - second);
  }

  customRollDimensionSuggestion(field) {
    if (!this.state.customSize) return '';
    const value = Number(this.state[field] || 0);
    if (!value) return '';

    const dimensions = this.customRollDimensions();
    if (!dimensions.length || dimensions.some((dimension) => Math.abs(dimension - value) < 0.0001)) return '';

    const lower = [...dimensions].reverse().find((dimension) => dimension < value);
    const upper = dimensions.find((dimension) => dimension > value);
    const nearby = [lower, upper].filter((dimension) => dimension !== undefined);
    if (!nearby.length) return '';

    return `Choose a nearby size: ${nearby.map((dimension) => `${formatSizeNumber(dimension)}″`).join(' or ')}.`;
  }

  customSizeValidationMessage() {
    if (!this.state.customSize) return '';
    const width = Number(this.state.width || 0);
    const height = Number(this.state.height || 0);
    if (!width || !height) return 'Enter a custom width and height';
    const limit = this.customSizeLimit();
    if (this.sizeBelowLimit(width, height, limit)) return limit.minimumMessage;
    if (this.sizeExceedsLimit(width, height, limit)) return limit.maximumMessage;
    return this.customRollDimensionSuggestion('width') || this.customRollDimensionSuggestion('height');
  }

  canContinueOptions() {
    return Boolean(this.price) && !this.isCustomSizeBlockingPrice();
  }

  continueBlockMessage() {
    if (this.state.customSize) {
      const message = this.customSizeValidationMessage();
      if (message) return message;
    }
    return 'Choose a valid size and quantity for instant pricing.';
  }

  isCustomSizeBlockingPrice(payload = this.pricePayload()) {
    if (!this.state.customSize && payload.size !== 'Custom') return false;
    const width = Number(payload.width || 0);
    const height = Number(payload.height || 0);
    if (!width || !height) return true;
    if (this.customRollDimensionSuggestion('width') || this.customRollDimensionSuggestion('height')) return true;
    return this.payloadOutsideProductSizeLimit(payload);
  }

  payloadOutsideProductSizeLimit(payload = this.pricePayload()) {
    const width = Number(payload.width || 0);
    const height = Number(payload.height || 0);
    const limit = this.customSizeLimit();
    return this.sizeBelowLimit(width, height, limit) || this.sizeExceedsLimit(width, height, limit);
  }

  sizeBelowLimit(width, height, limit) {
    if (!limit || !width || !height) return false;
    return width < Number(limit.minWidth || 0) || height < Number(limit.minHeight || 0);
  }

  sizeExceedsLimit(width, height, limit) {
    if (!limit || !width || !height) return false;
    if (limit.type === 'rectangle') {
      return Math.max(width, height) > limit.maxLongSide || Math.min(width, height) > limit.maxShortSide;
    }
    return width > limit.maxWidth || height > limit.maxHeight;
  }

  async updatePrice({ forceInlinePricePatch = false } = {}) {
    if (!this.profile) return;
    const patchInline = forceInlinePricePatch || this.shouldPatchPriceInline();
    if (this.isCustomSizeBlockingPrice()) {
      this.currentPriceRow = null;
      this.price = null;
      this.statusMessage = '';
      this.statusType = '';
      this.cancelConfiguredVariantPrewarm();
      if (patchInline) {
        this.patchInlinePricingUi();
        return;
      }
      this.render();
      return;
    }
    const localRow = this.findLocalPriceRow();
    if (localRow) {
      this.currentPriceRow = localRow;
      this.price = localRow.price;
      this.statusMessage = '';
      this.statusType = '';
      this.scheduleConfiguredVariantPrewarm();
      if (patchInline) {
        this.patchInlinePricingUi();
        return;
      }
      this.render();
      return;
    }

    this.currentPriceRow = null;
    if (this.localMatrix && !this.allowApiFallback) {
      this.price = null;
      this.statusMessage = 'Choose one of the available preset options for instant checkout pricing.';
      this.statusType = 'error';
      this.cancelConfiguredVariantPrewarm();
      if (patchInline) {
        this.patchInlinePricingUi();
        return;
      }
      this.render();
      return;
    }

    try {
      const response = await fetch(`${this.apiBase}/api/configurator/price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'bypass-tunnel-reminder': 'true' },
        body: JSON.stringify(this.pricePayload()),
      });
      const payload = await responseJson(response, 'Custom pricing is temporarily unavailable. Please choose a preset option or try again shortly.');
      if (!response.ok) throw new Error(payload.error || 'Pricing is not available.');
      this.price = payload.price;
      this.statusMessage = '';
      this.statusType = '';
      this.scheduleConfiguredVariantPrewarm();
    } catch (error) {
      this.price = null;
      this.statusMessage = error.message || 'Pricing is not available for this combination.';
      this.statusType = 'error';
      this.cancelConfiguredVariantPrewarm();
    }
    if (patchInline) {
      this.patchInlinePricingUi();
      return;
    }
    this.render();
  }

  shouldPatchPriceInline() {
    if (this.step !== 'options') return false;
    const active = document.activeElement;
    if (!active || !this.contains(active)) return false;
    const field = active.dataset?.input || '';
    if ((field === 'customWidth' || field === 'customHeight') && this.state.customSize) return true;
    if ((field === 'quantity' || field === 'labelQuantity') && this.state.customQuantity) return true;
    if (field === 'customArtworkVersions' && this.state.customArtworkVersions) return true;
    return false;
  }

  patchInlinePricingUi() {
    const priceBlockMarkup = this.renderPriceBlock(this.consumePriceAnimationState()).trim();
    this.querySelectorAll('[data-price]').forEach((priceBlock) => {
      const template = document.createElement('template');
      template.innerHTML = priceBlockMarkup;
      const nextBlock = template.content.firstElementChild;
      if (nextBlock) priceBlock.replaceWith(nextBlock);
    });

    this.patchStatusUi();

    if (this.state.customQuantity && document.activeElement?.closest('.spark-config__section--quantity')) {
      this.patchCustomQuantityResultRow();
    } else {
      this.patchQuantitySection();
    }

    if (this.profile.engine === 'sheets') this.patchSheetInfo();
    this.patchCustomSizeMessage();
    this.patchContinueButtons();
    this.updateStickyInputState();
  }

  patchContinueButtons() {
    const canContinue = this.canContinueOptions();
    this.querySelectorAll('[data-next-step]').forEach((button) => {
      button.disabled = !canContinue;
      button.setAttribute('aria-disabled', canContinue ? 'false' : 'true');
    });
  }

  patchStatusUi() {
    this.querySelectorAll('[data-status]').forEach((status) => {
      const actions = status.closest('.spark-config__actions');
      const actionRow = actions?.querySelector('.spark-config__action-row');
      if (actionRow && actionRow.nextElementSibling !== status) actionRow.after(status);
      status.className = `spark-config__status${this.statusType ? ` is-${this.statusType}` : ''}`;
      if (this.statusMessage) {
        status.textContent = this.statusMessage;
      } else {
        status.innerHTML = this.defaultStatusMarkup();
      }
    });
  }

  patchQuantitySection() {
    const current = this.querySelector('.spark-config__section--quantity');
    if (!current) return;
    const template = document.createElement('template');
    template.innerHTML = this.renderQuantitySection(this.profile.engine === 'sheets' ? 'Labels quantity' : 'Quantity').trim();
    const next = template.content.firstElementChild;
    if (!next) return;
    current.replaceWith(next);
    this.bindOptionEvents(next);
  }

  clearPresetQuantitySelection() {
    this.querySelectorAll('.spark-config__quantity-row:not([data-custom-quantity-result])').forEach((row) => {
      row.classList.remove('is-selected');
      row.setAttribute('aria-pressed', 'false');
    });
  }

  patchCustomQuantityResultRow() {
    const list = this.querySelector('.spark-config__quantity-list');
    if (!list) return;
    const field = this.quantityField();
    const isSheet = this.profile.engine === 'sheets';
    const basePrice = this.baseQuantityPrice(this.visibleQuantityValues(isSheet), field);
    const current = list.querySelector('[data-custom-quantity-result]');
    const html = this.renderCustomQuantityResultRow(field, isSheet, basePrice).trim();
    if (!html) {
      if (current) current.remove();
      return;
    }

    const template = document.createElement('template');
    template.innerHTML = html;
    const next = template.content.firstElementChild;
    if (!next) return;
    if (current) {
      current.replaceWith(next);
    } else {
      const customRow = list.querySelector('.spark-config__quantity-custom-row');
      if (customRow) {
        customRow.after(next);
      } else {
        list.append(next);
      }
    }
    this.bindOptionEvents(next);
  }

  patchSheetInfo() {
    const current = this.querySelector('.spark-config__facts');
    if (!current) return;
    const template = document.createElement('template');
    template.innerHTML = this.renderSheetInfo().trim();
    const next = template.content.firstElementChild;
    if (next) current.replaceWith(next);
  }

  patchCustomSizeMessage() {
    const widthMessage = this.customRollDimensionSuggestion('width');
    const heightMessage = this.customRollDimensionSuggestion('height');
    const rollMessage = widthMessage || heightMessage;
    const generalMessage = rollMessage ? '' : this.customSizeValidationMessage();
    const messages = { general: generalMessage, roll: rollMessage };
    this.querySelectorAll('[data-custom-size-message]').forEach((element) => {
      const key = element.dataset.customSizeMessage || 'general';
      const message = messages[key] || '';
      element.textContent = message;
      element.hidden = !message;
    });

    ['width', 'height'].forEach((field) => {
      const input = this.querySelector(`[data-input="custom${field[0].toUpperCase()}${field.slice(1)}"]`);
      if (!input) return;
      const fieldMessage = field === 'width' ? widthMessage : heightMessage;
      input.setAttribute('aria-invalid', fieldMessage ? 'true' : 'false');
      if (fieldMessage) {
        input.setAttribute('aria-describedby', 'spark-custom-roll-size-message');
      } else {
        input.removeAttribute('aria-describedby');
      }
    });
  }

  findLocalPriceRow() {
    if (!this.localMatrix?.rows?.length) return null;
    return this.findLocalPriceRowForPayload(this.pricePayload());
  }

  findLocalPriceRowForPayload(payload) {
    if (!this.localMatrix?.rows?.length) return null;
    if (this.payloadOutsideProductSizeLimit(payload)) return null;
    const exactRow = this.localMatrix.rows.find((row) => criteriaMatch(row.criteria || {}, payload)) || null;
    if (this.profile?.engine === 'stickers' && this.profile.priceProfile !== 'hang-tags') {
      // Saved preset rows predate size-boundary protection. Recalculate every
      // sticker quote; reuse a saved variant only when its actual cents match.
      const price = this.calculatedLocalPriceForPayload(payload);
      if (!price) return null;
      const matchingVariant = exactRow && Number(exactRow.price?.total) === price.total
        ? exactRow : this.findPriceVariantForTotal(price.total);
      return {
        key: `calculated:${this.productHandle}:${stablePayloadKey(payload)}`,
        criteria: {...payload},
        price,
        variantTitle: configuredVariantLabel(price.total),
        variantId: matchingVariant?.variantId || '',
        variantGid: matchingVariant?.variantGid || '',
      };
    }
    if (exactRow && payload.lamination !== 'matched') return exactRow;
    if (exactRow && payload.lamination === 'matched' && this.laminationTypeForMaterial(payload.material)) {
      // Apply lamination before the final currency rounding, just like the
      // server and custom-size calculator. Multiplying a rounded preset price
      // can differ by a cent and correctly trigger the checkout price guard.
      const price = this.calculatedLocalPriceForPayload(payload);
      if (!price) return null;
      return {
        ...exactRow,
        key: `${exactRow.key}|lamination:matched`,
        criteria: { ...exactRow.criteria, lamination: 'matched' },
        price,
        variantTitle: configuredVariantLabel(price.total),
        variantId: '',
        variantGid: '',
      };
    }

    const indexedRollRow = this.rollLabelCustomRowForPayload(payload);
    if (indexedRollRow) return indexedRollRow;

    const calculatedPrice = this.calculatedLocalPriceForPayload(payload);
    if (!calculatedPrice) return null;

    const priceVariant = this.findPriceVariantForTotal(calculatedPrice.total);
    return {
      key: `calculated:${this.productHandle}:${stablePayloadKey(payload)}`,
      criteria: { ...payload },
      price: calculatedPrice,
      variantTitle: configuredVariantLabel(calculatedPrice.total),
      variantId: priceVariant?.variantId || '',
      variantGid: priceVariant?.variantGid || '',
    };
  }

  rollLabelCustomRowForPayload(payload) {
    if (this.profile?.engine !== 'vista-roll-labels') return null;
    const indexRows = this.localMatrix?.customRollPriceIndex?.rows || null;
    if (!indexRows) return null;

    const width = Number(payload.width || 0);
    const height = Number(payload.height || 0);
    const material = payload.material || this.profile.defaultMaterial || 'White Paper';
    const finish = payload.finish || this.profile.defaultFinish || 'Gloss';
    const quantity = toInteger(payload.quantity);

    if (!width || !height || !quantity) return null;

    const widthIndex = indexValueIndex(this.localMatrix.customRollPriceIndex.dimensions, formatSizeNumber(width));
    const heightIndex = indexValueIndex(this.localMatrix.customRollPriceIndex.dimensions, formatSizeNumber(height));
    const materialIndex = indexValueIndex(this.localMatrix.customRollPriceIndex.materials, material);
    const finishIndex = indexValueIndex(this.localMatrix.customRollPriceIndex.finishes, finish);
    const quantityIndex = indexValueIndex(this.localMatrix.customRollPriceIndex.quantities, quantity);

    if ([widthIndex, heightIndex, materialIndex, finishIndex, quantityIndex].some((index) => index < 0)) return null;

    const keys = [
      rollLabelCustomIndexKey(widthIndex, heightIndex, materialIndex, finishIndex, quantityIndex),
    ];

    if (width !== height) {
      keys.push(rollLabelCustomIndexKey(heightIndex, widthIndex, materialIndex, finishIndex, quantityIndex));
    }

    const matchKey = keys.find((key) => indexRows[key]);
    if (!matchKey) return null;

    const compact = indexRows[matchKey];
    const total = Array.isArray(compact) ? Number(compact[0] || 0) : Number(compact || 0);
    const variant = this.rollLabelCustomPriceVariant(total);
    const sizeLabel = formatSizeLabel(width, height, true);
    const price = {
      total,
      discount: 0,
      originalTotal: total,
      quantity,
      key: `Custom|Custom|${formatSizeNumber(width)}|${formatSizeNumber(height)}|${material}|${finish}|${quantity}`,
      sizeLabel,
      material,
      finish,
      shape: 'Custom',
    };

    return {
      key: `roll-custom:${matchKey}`,
      criteria: {
        shape: 'Custom',
        width,
        height,
        material,
        finish,
        quantity,
      },
      price,
      variantTitle: variant?.title || configuredVariantLabel(price.total),
      variantId: variant?.variantId || '',
      variantGid: variant?.variantGid || '',
    };
  }

  rollLabelCustomPriceVariant(total) {
    const priceKey = Number(total || 0).toFixed(2);
    const variant = this.localMatrix.customRollPriceIndex.priceVariants?.[priceKey] || null;
    if (!variant) return null;
    if (Array.isArray(variant)) {
      const variantId = variant[0] || '';
      return {
        title: configuredVariantLabel(total),
        variantId,
        variantGid: variant[1] || (variantId ? `gid://shopify/ProductVariant/${variantId}` : ''),
      };
    }
    return variant;
  }

  priceForOverrides(overrides = {}) {
    const row = this.findLocalPriceRowForPayload({ ...this.pricePayload(), ...overrides });
    return row?.price || null;
  }

  variantIdForOverrides(overrides = {}) {
    if (this.usesBaseCurrency()) return '';
    const row = this.findLocalPriceRowForPayload({ ...this.pricePayload(), ...overrides });
    return row?.variantId ? String(row.variantId) : '';
  }

  usesBaseCurrency() {
    return String(this.currency || SHOP_BASE_CURRENCY).toUpperCase() === SHOP_BASE_CURRENCY;
  }

  // Text for one configured amount. Base-currency markets keep the calculated
  // total. Other markets show the cached Shopify price of the exact checkout
  // variant, or a neutral placeholder until it is known.
  moneyText(total, variantId, current = false) {
    if (this.usesBaseCurrency()) return formatMoney(total, this.currency);
    const id = variantId ? String(variantId) : '';
    if (!id) return current && this.displayVariantPending() ? PRESENTMENT_PRICE_PENDING : PRESENTMENT_PRICE_UNAVAILABLE;
    const cents = this.presentmentPrices.get(id);
    if (Number.isSafeInteger(cents)) return formatMoney(cents / 100, this.currency);
    if (cents === null) return PRESENTMENT_PRICE_UNAVAILABLE;
    this.requestPresentmentPrice(id);
    return PRESENTMENT_PRICE_PENDING;
  }

  moneyMarkup(total, variantId, current = false) {
    const text = this.moneyText(total, variantId, current);
    if (this.usesBaseCurrency()) return text;
    const id = variantId ? String(variantId) : '';
    if (id) return `<span data-presentment-variant="${escapeHtml(id)}">${escapeHtml(text)}</span>`;
    return current ? `<span data-presentment-current>${escapeHtml(text)}</span>` : escapeHtml(text);
  }

  // The variant checkout will charge for the current selection: the preset
  // row's variant, or the custom variant prepared for this exact payload.
  displayVariantId() {
    const rowVariantId = this.currentPriceRow?.variantId;
    if (rowVariantId) return String(rowVariantId);
    if (this.usesBaseCurrency() || !this.price) return '';
    const entry = this.preparedVariantCache?.get(this.activePreparedVariantKey);
    return entry?.state === 'resolved' && entry.variantId ? String(entry.variantId) : '';
  }

  displayVariantPending() {
    if (!this.price || this.currentPriceRow?.variantId) return false;
    const entry = this.preparedVariantCache?.get(this.activePreparedVariantKey);
    return Boolean(this.preparedVariantTimer || entry?.state === 'pending');
  }

  handlePreparedVariantResolved(key) {
    if (this.usesBaseCurrency() || key !== this.activePreparedVariantKey) return;
    const id = this.displayVariantId();
    if (!id) return;
    this.querySelectorAll('[data-presentment-current]').forEach((element) => {
      element.removeAttribute('data-presentment-current');
      element.dataset.presentmentVariant = id;
    });
    if (Number.isSafeInteger(this.presentmentPrices.get(id))) this.applyPresentmentPrice(id);
    else this.requestPresentmentPrice(id);
  }

  addToCartLabel() {
    if (this.loading) return 'Adding...';
    return `Add to cart — ${this.price ? this.moneyText(this.price.total, this.displayVariantId(), true) : 'Choose options'}`;
  }

  requestPresentmentPrice(variantId) {
    const id = String(variantId || '');
    if (!/^\d+$/.test(id) || this.presentmentPrices.has(id) || this.presentmentPriceRequests.has(id)) return;
    const root = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const request = fetch(`${root}variants/${id}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((variant) => {
        const cents = Number(variant?.price);
        return String(variant?.id) === id && Number.isSafeInteger(cents) && cents > 0 ? cents : null;
      })
      .catch(() => null)
      .then((cents) => {
        this.presentmentPriceRequests.delete(id);
        this.presentmentPrices.set(id, cents);
        this.applyPresentmentPrice(id);
      });
    this.presentmentPriceRequests.set(id, request);
  }

  applyPresentmentPrice(variantId) {
    const text = this.moneyText(0, variantId);
    this.querySelectorAll('[data-presentment-variant]').forEach((element) => {
      if (element.dataset.presentmentVariant === variantId) element.textContent = text;
    });
    this.updateAddButtonState();
  }

  calculatedLocalPriceForPayload(payload) {
    if (!this.profile?.settings || !payload) return null;
    if (this.profile.engine === 'stickers') return calculateStickerPrice(this.profile.settings, payload, this.profile);
    if (this.profile.engine === 'sheets') return calculateSheetPrice(this.profile.settings, payload, this.profile);
    if (this.profile.engine === 'jukebox-magnets') return calculateJukeboxMagnetPrice(this.profile.settings, payload);
    return null;
  }

  findPriceVariantForTotal(total) {
    const price = roundMoney(total);
    const variantTitle = configuredVariantLabel(price);
    const productVariant = (this.localMatrix?.priceVariants || []).find((variant) => (
      variant.title === variantTitle || Number(variant.price).toFixed(2) === price.toFixed(2)
    )) || null;
    if (productVariant) return productVariant;

    // Custom prices on API-enabled products must be created on the public
    // product. A shared hidden price-bank variant exposes its backend product
    // title in Shopify checkout, where Basic plans cannot override line titles.
    if (this.allowApiFallback) return null;

    return this.customPriceBankVariant(price);
  }

  customPriceBankVariant(total) {
    const priceKey = Number(total || 0).toFixed(2);
    const variant = this.localMatrix?.customPriceBank?.priceVariants?.[priceKey] || null;
    if (!variant) return null;
    if (Array.isArray(variant)) {
      const variantId = variant[0] || '';
      return {
        title: configuredVariantLabel(total),
        variantId,
        variantGid: variant[1] || (variantId ? `gid://shopify/ProductVariant/${variantId}` : ''),
      };
    }
    return variant;
  }

  pricePayload() {
    const payload = {
      productHandle: this.productHandle,
      shape: this.state.shape,
      material: this.state.material,
      finish: this.state.finish,
      lamination: this.state.lamination,
      width: this.state.width,
      height: this.state.height,
      size: this.state.size,
      sizeLabel: this.state.sizeLabel,
      quantity: this.state.quantity,
      labelQuantity: this.state.labelQuantity,
      orientation: this.state.orientation,
      artworkVersions: this.state.artworkVersions,
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined || payload[key] === '') delete payload[key];
    });

    return payload;
  }

  async addToCart() {
    if (this.loading || !this.profile) return;

    const artworkFiles = this.selectedArtworkFiles();
    const hasRequiredArtworkFiles = artworkFiles.length === this.requiredArtworkFileCount();
    const willEmailArtwork = this.state.artworkMode === 'email_later';

    if (!hasRequiredArtworkFiles && !willEmailArtwork) {
      this.statusMessage = `Please select exactly ${this.requiredArtworkFileCount()} artwork ${this.requiredArtworkFileCount() === 1 ? 'file' : 'files'} before adding this item.`;
      this.statusType = 'error';
      this.updateAddButtonState();
      return;
    }

    const oversizedFile = artworkFiles.find((file) => file.size > R2_ARTWORK_FILE_LIMIT_BYTES);
    if (oversizedFile) {
      this.statusMessage = `${oversizedFile.name} is too large. Maximum file size is 50 MB.`;
      this.statusType = 'error';
      this.render();
      this.focusArtworkStep();
      return;
    }

    const combinedArtworkBytes = artworkFiles.reduce((total, file) => total + Number(file.size || 0), 0);
    if (combinedArtworkBytes > R2_ARTWORK_TOTAL_LIMIT_BYTES) {
      this.statusMessage = 'The combined artwork upload is too large. Maximum combined size is 500 MB.';
      this.statusType = 'error';
      this.render();
      this.focusArtworkStep();
      return;
    }

    const unsupportedFile = artworkFiles.find((file) => !isAllowedArtworkFile(file.name));
    if (unsupportedFile) {
      this.statusMessage = 'Accepted artwork files are JPG, JPEG, PNG, PDF, AI, EPS, TIF, TIFF, or SVG.';
      this.statusType = 'error';
      this.render();
      this.focusArtworkStep();
      return;
    }

    this.loading = true;
    this.statusMessage = 'Hang tight! We’re preparing your custom item and adding it to your cart…';
    this.statusType = '';
    this.render();

    try {
      if (!willEmailArtwork) {
        await this.ensureArtworkUploaded(artworkFiles);
      }

      let price = this.price;
      let variantId = this.currentPriceRow?.variantId;

      if (!variantId && (!this.apiBase || !this.allowApiFallback)) {
        throw new Error('This custom price is not ready for checkout yet. Please choose a preset option or contact us for help.');
      }

      if (!variantId) {
        const prepared = await this.prepareConfiguredVariant(this.pricePayload(), undefined, { forCart: true });
        this.assertPreparedPrice(prepared, price);
        price = prepared.price;
        variantId = prepared.variant.id;
      }

      const cartPayload = await this.addConfiguredVariantToCart({
        variantId,
        price,
        artworkFiles,
        retryUnavailable: true,
      });

      const addedVariantId = cartPayload.variant_id || cartPayload.id || variantId;
      this.persistConfigurationSnapshot(addedVariantId);
      await this.openCartDrawer(cartPayload, addedVariantId);
    } catch (error) {
      this.loading = false;
      this.statusMessage = isTransientConfiguredVariantError(error.message)
        ? 'Something went wrong while adding this item. Please try Add to cart again now.'
        : customerSafeError(error.message, 'Could not add configured item to cart.');
      this.statusType = 'error';
      this.render();
      this.focusArtworkStep();
    }
  }

  cartFormData({ variantId, price, artworkFiles }) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', 'cart-drawer-section');
    formData.append('sections_url', window.location.pathname);
    this.cartProperties(price, artworkFiles).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        formData.append(`properties[${key}]`, value);
      }
    });

    return formData;
  }

  async addConfiguredVariantToCart({ variantId, price, artworkFiles, retryUnavailable = false }) {
    const attemptId = crypto.randomUUID();
    const retryDelays = retryUnavailable ? [0, 700, 1300, 2200, 3500, 5000] : [0];
    let activeVariantId = variantId;
    let activePrice = price;
    let lastError = null;

    for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
      const delay = retryDelays[attempt];
      if (delay > 0) await sleep(delay);

      const cartResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Spark-Cart-Attempt': attemptId },
        body: this.cartFormData({ variantId: activeVariantId, price: activePrice, artworkFiles }),
      });
      const cartPayload = await responseJson(cartResponse, 'Could not add item to cart.');

      if (cartResponse.ok) {
        return cartPayload;
      }

      lastError = new Error(cartPayload.description || cartPayload.message || 'Could not add item to cart.');
      if (!retryUnavailable || cartResponse.status !== 422 || !isTransientConfiguredVariantError(lastError.message) || attempt === retryDelays.length - 1) {
        throw lastError;
      }

      // A published pricing asset can briefly contain a Shopify variant ID
      // that was replaced or removed during a price-bank sync. Re-resolve the
      // configured variant from the service before retrying so customers do
      // not get stuck on a 422 "Cannot find variant" cart error.
      // Variant recovery is safe for every API-backed product, even when the
      // normal API fallback flag is disabled. That flag controls missing or
      // custom local prices; this path only repairs a stale Shopify ID after
      // Shopify has explicitly rejected it.
      if (this.apiBase && /cannot find variant|variant.*not found/i.test(lastError.message)) {
        try {
          // Do not reuse an entry that Shopify has explicitly rejected.
          this.preparedVariantCache.forEach((entry, key) => {
            if (entry.state === 'resolved' && String(entry.variantId) === String(activeVariantId)) this.preparedVariantCache.delete(key);
          });
          const prepared = await this.prepareConfiguredVariant(this.pricePayload(), undefined, { forCart: true });
          if (prepared?.variant?.id) {
            this.assertPreparedPrice(prepared, price);
            activeVariantId = prepared.variant.id;
            activePrice = prepared.price || activePrice;
          }
        } catch (refreshError) {
          if (refreshError.code === 'SPARK_QUOTE_CHANGED') throw refreshError;
          if (refreshError.name !== 'AbortError') {
            console.warn('SparkStickers configured variant refresh failed.', refreshError);
          }
        }
      }
    }

    throw lastError || new Error('Could not add item to cart.');
  }

  assertPreparedPrice(prepared, quotedPrice) {
    const quoted = Math.round(Number(quotedPrice?.total) * 100);
    if (!(quoted > 0) || Math.round(Number(prepared?.price?.total) * 100) !== quoted || Math.round(Number(prepared?.variant?.price) * 100) !== quoted) {
      const error = new Error('The checkout price has changed. Please refresh the page and review your total before adding to cart.');
      error.code = 'SPARK_QUOTE_CHANGED';
      if (this.apiBase && this.productHandle) {
        const selected = this.pricePayload();
        fetch(`${this.apiBase}/api/configurator/cart-failure`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
          body: JSON.stringify({
            code: 'configured_cart_failed', productHandle: this.productHandle, stage: 'cart-add',
            variantId: String(prepared?.variant?.id || ''), shape: selected.shape, size: selected.sizeLabel || selected.size,
            quantity: selected.labelQuantity || selected.quantity, material: selected.material, finish: selected.finish,
            lamination: selected.lamination, displayedPrice: String(quotedPrice?.total || ''),
            error: `Checkout price mismatch: quoted ${quotedPrice?.total}, prepared ${prepared?.price?.total}, variant ${prepared?.variant?.price}`,
          }),
        }).catch(() => {});
      }
      throw error;
    }
  }

  configuredVariantNeedsPrewarm(payload = this.pricePayload()) {
    if (!this.profile || !this.allowApiFallback || !this.apiBase) return false;
    if (!this.price || this.isCustomSizeBlockingPrice(payload)) return false;
    if (this.currentPriceRow?.variantId) return false;
    return true;
  }

  preparedVariantKey(payload = this.pricePayload(), price = this.price) {
    const total = Number(price?.total || 0).toFixed(2);
    return `${stablePayloadKey(payload)}|total:${total}`;
  }

  clearPreparedVariantTimer() {
    if (this.preparedVariantTimer) {
      window.clearTimeout(this.preparedVariantTimer);
      this.preparedVariantTimer = null;
    }
  }

  cancelPreparedVariantRequestsExcept(activeKey) {
    if (!this.preparedVariantCache) return;
    this.preparedVariantCache.forEach((entry, key) => {
      if (key === activeKey) return;
      // Once Add to cart uses a prewarm, option/price refreshes must not abort it.
      if (entry?.state === 'pending' && entry.forCart) return;
      if (entry?.state === 'pending') entry.controller?.abort({
        name: 'AbortError',
        message: 'Background preparation was superseded.',
        code: 'SPARK_PREWARM_SUPERSEDED',
      });
      this.preparedVariantCache.delete(key);
    });
  }

  cancelConfiguredVariantPrewarm() {
    this.clearPreparedVariantTimer();
    this.activePreparedVariantKey = '';
    this.cancelPreparedVariantRequestsExcept('');
  }

  scheduleConfiguredVariantPrewarm() {
    this.clearPreparedVariantTimer();
    const payload = this.pricePayload();

    if (!this.configuredVariantNeedsPrewarm(payload)) {
      this.cancelConfiguredVariantPrewarm();
      return;
    }

    const key = this.preparedVariantKey(payload);
    this.activePreparedVariantKey = key;
    this.cancelPreparedVariantRequestsExcept(key);

    if (this.preparedVariantCache.has(key)) return;

    const payloadSnapshot = { ...payload };
    this.preparedVariantTimer = window.setTimeout(() => {
      this.preparedVariantTimer = null;
      this.prepareConfiguredVariant(payloadSnapshot, key).catch((error) => {
        if (error.name !== 'AbortError') {
          console.warn('SparkStickers configured variant prewarm failed.', error);
        }
      });
    }, 750);
  }

  async prepareConfiguredVariant(payload = this.pricePayload(), key = this.preparedVariantKey(payload), { forCart = false } = {}) {
    const cached = this.preparedVariantCache.get(key);
    if (cached && !cached.controller?.signal.aborted && cached.state !== 'rejected') {
      if (forCart) cached.forCart = true;
      return cached.promise;
    }

    const controller = new AbortController();
    const entry = {
      state: 'pending',
      forCart,
      controller,
      promise: null,
    };

    entry.promise = (async () => {
      let waitedMs = 0;
      for (let attempt = 0; ; attempt++) {
        const response = await fetch(`${this.apiBase}/api/configurator/cart-variant`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'bypass-tunnel-reminder': 'true' },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        const responsePayload = await responseJson(response, 'Custom checkout pricing is temporarily unavailable. Please choose a preset option or try again shortly.');
        if (response.ok) {
          entry.state = 'resolved';
          entry.variantId = responsePayload.variant?.id;
          this.handlePreparedVariantResolved(key);
          return responsePayload;
        }
        const retry = responsePayload.preparationRetry;
        const delayMs = Number(retry?.afterSeconds) * 1000;
        // Only an explicit server guarantee that no write began permits a retry.
        // Network errors, HTML errors and uncertain writes never enter this path.
        if (!entry.forCart || response.status !== 503 || retry?.safe !== true || retry.reason !== 'read-before-write'
          || !Number.isFinite(delayMs) || delayMs <= 0 || attempt >= 2 || waitedMs + delayMs > 150000) {
          throw new Error(responsePayload.error || 'Could not create configured item.');
        }
        waitedMs += delayMs;
        await this.waitForPreparationRetry(entry, delayMs);
      }
    })()
      .catch((error) => {
        if (this.preparedVariantCache.get(key) === entry) this.preparedVariantCache.delete(key);
        entry.state = 'rejected';
        throw error;
      });

    this.preparedVariantCache.set(key, entry);
    return entry.promise;
  }

  async waitForPreparationRetry(entry, delayMs) {
    const deadline = Date.now() + delayMs;
    do {
      if (entry.controller.signal.aborted) throw entry.controller.signal.reason || new DOMException('Cancelled', 'AbortError');
      const remaining = Math.max(0, deadline - Date.now());
      if (!remaining) break;
      this.statusMessage = `Checkout is temporarily busy. Your choices stay here. Retrying in ${Math.ceil(remaining / 1000)} seconds...`;
      this.statusType = '';
      const status = this.querySelector('[data-status]');
      if (status) { status.textContent = this.statusMessage; status.classList.remove('is-error'); }
      await new Promise((resolve) => window.setTimeout(resolve, Math.min(1000, remaining)));
    } while (true);
    if (entry.controller.signal.aborted) throw entry.controller.signal.reason || new DOMException('Cancelled', 'AbortError');
    this.statusMessage = 'Reconnecting checkout and preparing your item...';
    const status = this.querySelector('[data-status]');
    if (status) status.textContent = this.statusMessage;
  }

  cartProperties(price, artworkFiles) {
    const selectedFiles = Array.isArray(artworkFiles) ? artworkFiles : [];
    const finish = String(this.finishLabel(this.state.finish) || '').trim();
    const meaningfulFinish = !['', 'none', 'no foil', 'no finish'].includes(finish.toLowerCase());
    const artworkVersionCount = Math.max(1, Number(price.artworkVersions || this.state.artworkVersions || 1));
    const artworkValue = this.state.artworkMode === 'email_later'
      ? 'I will email artwork later.'
      : (this.artworkManifestToken && selectedFiles[0]?.name ? `Received — ${selectedFiles[0].name}` : selectedFiles[0]?.name);
    const props = [
      ['_Configured product', this.profile.productLabel || this.productTitle],
      // Configured base-currency amount for purchasing diagnostics; checkout
      // charges the selected variant's market price.
      ['_Configured price', formatMoney(price.total, SHOP_BASE_CURRENCY)],
      ['_Product handle', this.productHandle],
      ['_Configuration', this.configurationToken()],
      ['_Shipping mode', this.profile.shipping?.mode || 'calculated'],
      ['_Shipping group', this.profile.shipping?.shippingGroup || 'general'],
    ];
    if (this.state.artworkMode !== 'email_later' && this.artworkManifestToken) {
      props.push(['_Artwork manifest', this.artworkManifestToken]);
      props.push(['_Artwork access', this.artworkAccessUrl]);
    }

    if (this.profile.engine === 'vista-roll-labels') {
      props.push(['Roll direction', this.state.unwindDirection || 'Right of Copy']);
    }
    if (!this.profile.lockedShape) props.push(['Shape', this.shapeLabel(this.state.shape)]);
    props.push(['Size', this.state.sizeLabel || this.state.size]);
    if (this.state.material) props.push(['Material', this.materialLabel(this.state.material)]);
    if (meaningfulFinish) props.push(['Finish', finish]);
    if (this.state.lamination === 'matched') {
      props.push(['Lamination', `${this.laminationTypeForMaterial(this.state.material) === 'matte' ? 'Matte' : 'Gloss'} Lamination`]);
    }
    props.push(['Sheet quantity', price.sheetQuantity ? `${price.sheetQuantity} sheets` : '']);
    props.push(['Labels per sheet', price.sheetFit || '']);
    if (this.profile.engine === 'sheets' && artworkVersionCount > 1) {
      props.push(['Artwork versions', artworkVersionCount]);
    }
    if (this.state.orientation && this.profile.engine !== 'vista-magnets') props.push(['Orientation', this.state.orientation]);
    props.push(['Artwork', artworkValue]);
    selectedFiles.slice(1).forEach((file, index) => {
      props.push([`Artwork ${index + 2}`, file.name]);
    });
    props.push(['Artwork notes', this.querySelector('[data-input="artworkNotes"]')?.value || '']);
    const reminderEmail = String(this.querySelector('[data-input="artworkReminderEmail"]')?.value || '').trim();
    if (this.state.artworkMode === 'email_later' && reminderEmail) {
      props.push(['Artwork reminder email', reminderEmail]);
    }
    props.push(['Quantity', this.profile.engine === 'sheets' ? `${price.labelQuantity} labels` : `${price.quantity || this.state.quantity}`]);
    props.push(['Digital proof', 'You’ll receive a digital proof to approve before we print.']);
    return props;
  }

  renderShapeSection() {
    const order = ['diecut', 'circle', 'square', 'rectangle', 'rounded', 'oval'];
    const shapes = this.profile.settings?.shapes || {};
    const options = order.filter((key) => shapes[key]).map((key) => ({ value: key, label: shapes[key] }));
    return this.renderChoiceSection('Shape', 'shape', options);
  }

  renderSizeSection() {
    const options = this.stickerSizes();
    return this.renderSizeChoiceSection('Size', [...options, { value: 'Custom', label: 'Custom Size' }]);
  }

  renderSizeChoiceSection(title, options) {
    if (!options?.length) return '';
    const layoutClass = this.profile?.engine === 'vista-roll-labels'
      ? ' spark-config__section--roll-size'
      : ' spark-config__section--sticker-size';
    return `
      <div class="spark-config__section spark-config__section--size${layoutClass}">
        <h3>${escapeHtml(this.selectionTitle(title))}</h3>
        <div class="spark-config__choices">
          ${options.map((option) => {
            const isCustom = option.value === 'Custom';
            if (isCustom && this.state.customSize) {
              const widthMessage = this.customRollDimensionSuggestion('width');
              const heightMessage = this.customRollDimensionSuggestion('height');
              const rollMessage = widthMessage || heightMessage;
              const generalMessage = rollMessage ? '' : this.customSizeValidationMessage();
              return `
                <div class="spark-config__custom-size-fields">
                  <div class="spark-config__custom-size-field">
                    <input data-input="customWidth" type="text" inputmode="decimal" pattern="[0-9]*[.]?[0-9]*" autocomplete="off" value="${escapeHtml(this.state.width || '')}" placeholder="Width" aria-label="Custom width" aria-invalid="${widthMessage ? 'true' : 'false'}"${widthMessage ? ' aria-describedby="spark-custom-roll-size-message"' : ''}>
                  </div>
                  <div class="spark-config__custom-size-field">
                    <input data-input="customHeight" type="text" inputmode="decimal" pattern="[0-9]*[.]?[0-9]*" autocomplete="off" value="${escapeHtml(this.state.height || '')}" placeholder="Height" aria-label="Custom height" aria-invalid="${heightMessage ? 'true' : 'false'}"${heightMessage ? ' aria-describedby="spark-custom-roll-size-message"' : ''}>
                  </div>
                  <p id="spark-custom-roll-size-message" data-custom-size-message="roll" aria-live="polite"${rollMessage ? '' : ' hidden'}>${escapeHtml(rollMessage)}</p>
                  <p data-custom-size-message="general" aria-live="polite"${generalMessage ? '' : ' hidden'}>${escapeHtml(generalMessage)}</p>
                </div>
              `;
            }

            const selected = isCustom ? this.state.customSize : String(this.state.size) === String(option.value);
            return `
              <button
                type="button"
                class="spark-config__choice${selected ? ' is-selected' : ''}"
                data-choice
                data-field="size"
                data-value="${escapeHtml(option.value)}"
              >
                <span>${escapeHtml(option.label)}</span>
              </button>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  stickerSizes() {
    const rectangleLike = ['rectangle', 'oval'].includes(this.state.shape);
    const labels = rectangleLike
      ? ['2" x 1"', '3" x 2"', '4" x 3"', '5" x 3"']
      : ['2" x 2"', '3" x 3"', '4" x 4"', '5" x 5"'];

    return labels.map((label) => ({ value: label, label }));
  }

  renderSheetInfo() {
    return `
      <div class="spark-config__facts">
        <div class="spark-config__facts-row">
          <span class="spark-config__fact"><span>Sheet size:</span><strong>8.5" x 11"</strong></span>
          <span class="spark-config__fact"><span>Labels per sheet:</span><strong>${this.price?.sheetFit || '-'}</strong></span>
        </div>
      </div>
    `;
  }

  renderMaterialSection() {
    const materials = this.materialOptions();
    if (!materials.length) return '';
    return this.renderChoiceSection('Material', 'material', materials);
  }

  materialOptions() {
    const profile = this.profile;
    if (profile.engine === 'vista-roll-labels') {
      const values = profile.materialOrder || profile.settings?.rollLabels?.materials || [];
      return values.map((value) => ({ value, label: value }));
    }

    const source = profile.engine === 'sheets' ? profile.settings?.sheetMaterials : profile.settings?.materials;
    return Object.entries(source || {}).map(([value, material]) => {
      if (profile.engine === 'stickers') {
        if (value === 'vinylmatte') return { value, label: 'Matte' };
        if (value === 'vinylglossy') return { value, label: 'Glossy' };
      }
      return { value, label: material.label || value };
    });
  }

  renderFinishSection() {
    const options = this.sheetFinishOptions();
    return this.renderChoiceSection('Finish', 'finish', options);
  }

  sheetFinishOptions(material = this.state.material) {
    const source = this.profile.settings?.sheetFinishes || {};
    return Object.entries(source)
      .filter(([value]) => isSheetFinishAvailableForMaterial(material, value))
      .map(([value, finish]) => ({ value, label: finish.label || value }));
  }

  normalizedSheetFinish(finish, material = this.state.material) {
    const options = this.sheetFinishOptions(material);
    const values = options.map((option) => option.value);
    if (values.includes(finish)) return finish;
    if (values.includes(this.profile.defaultFinish)) return this.profile.defaultFinish;
    return values[0] || 'none';
  }

  renderArtworkVersionsSection() {
    const selectedLabel = this.artworkVersionsLabel();
    const options = [
      { value: '1', label: '1 Artwork' },
      { value: '2', label: '2 Artworks' },
      { value: '3', label: '3 Artworks' },
      { value: 'custom', label: 'Custom Artwork Quantity' },
    ];

    return `
      <div class="spark-config__section spark-config__section--versions">
        <div class="spark-config__section-title-row">
          <h3>${escapeHtml(this.selectionTitle('Versions'))}</h3>
          <button class="spark-config__help-trigger" type="button" data-versions-help-open aria-label="More information about artwork versions">?</button>
          <dialog class="spark-config__help-dialog" data-versions-help-dialog aria-labelledby="spark-versions-help-title">
            <div class="spark-config__help-dialog-inner">
              <button class="spark-config__help-close" type="button" data-versions-help-close aria-label="Close artwork versions information">×</button>
              <h4 id="spark-versions-help-title">What are artwork versions?</h4>
              <p>Artwork versions are different designs in the same order, such as different logos, names, or label designs. Choose the number of unique designs you are supplying, not the number of printed pieces. Each design uses the same selected size, material, and finish.</p>
            </div>
          </dialog>
        </div>
        <details class="spark-config__versions-dropdown"${this.versionsDropdownOpen ? ' open' : ''}>
          <summary>
            <span data-versions-summary>${escapeHtml(selectedLabel)}</span>
            <span class="spark-config__versions-chevron" aria-hidden="true"></span>
          </summary>
          <div class="spark-config__versions-panel">
            ${options.map((option) => `
              <button
                type="button"
                class="spark-config__versions-option${(option.value === 'custom' ? this.state.customArtworkVersions : (!this.state.customArtworkVersions && String(this.state.artworkVersions) === option.value)) ? ' is-selected' : ''}"
                data-choice
                data-field="artworkVersions"
                data-value="${option.value}"
              >${escapeHtml(option.label)}</button>
              ${option.value === 'custom' && this.state.customArtworkVersions ? `
                <div class="spark-config__versions-custom">
                  <label for="spark-custom-artwork-versions">Number of artworks</label>
                  <input id="spark-custom-artwork-versions" data-input="customArtworkVersions" type="number" inputmode="numeric" min="1" step="1" value="${escapeHtml(this.state.artworkVersions || '')}" placeholder="Enter artwork quantity">
                </div>
              ` : ''}
            `).join('')}
          </div>
        </details>
      </div>
    `;
  }

  artworkVersionsLabel() {
    return this.state.customArtworkVersions
      ? `Custom: ${formatNumber(this.state.artworkVersions || 1)} Artworks`
      : ({ 1: '1 Artwork', 2: '2 Artworks', 3: '3 Artworks' }[this.state.artworkVersions] || '1 Artwork');
  }

  patchArtworkVersionsSummary() {
    const summary = this.querySelector('[data-versions-summary]');
    if (summary) summary.textContent = this.artworkVersionsLabel();
  }

  isRoundedShape(value) {
    return ['rounded', 'roundedsquare'].includes(String(value || '').toLowerCase().replace(/[\s-]+/g, ''));
  }

  visibleShapeOptions(options) {
    if (this.profile?.engine === 'vista-roll-labels') return options;
    return (options || []).filter((option) => !this.isRoundedShape(option.value));
  }

  normalizeVisibleShapeSelection() {
    if (this.profile?.engine === 'vista-roll-labels' || !this.isRoundedShape(this.state?.shape)) return;

    const options = this.profile?.engine === 'vista-magnets'
      ? this.magnetShapes()
      : ['diecut', 'circle', 'square', 'rectangle', 'rounded', 'oval']
        .filter((key) => this.profile?.settings?.shapes?.[key])
        .map((key) => ({ value: key, label: this.profile.settings.shapes[key] }));
    const fallback = this.visibleShapeOptions(options)[0];
    if (fallback) this.state.shape = fallback.value;
  }

  patchArtworkVersionsControl() {
    const dropdown = this.querySelector('.spark-config__versions-dropdown');
    const panel = dropdown?.querySelector('.spark-config__versions-panel');
    if (!dropdown || !panel) return;

    this.versionsDropdownOpen = false;
    dropdown.removeAttribute('open');
    this.patchArtworkVersionsSummary();

    const selectedValue = this.state.customArtworkVersions ? 'custom' : String(this.state.artworkVersions);
    panel.querySelectorAll('.spark-config__versions-option').forEach((option) => {
      option.classList.toggle('is-selected', option.dataset.value === selectedValue);
    });

    const currentCustom = panel.querySelector('.spark-config__versions-custom');
    if (!this.state.customArtworkVersions) {
      currentCustom?.remove();
      return;
    }

    if (currentCustom) {
      const input = currentCustom.querySelector('[data-input="customArtworkVersions"]');
      if (input) input.value = String(this.state.artworkVersions || 1);
      return;
    }

    const customOption = panel.querySelector('[data-field="artworkVersions"][data-value="custom"]');
    if (!customOption) return;
    const custom = document.createElement('div');
    custom.className = 'spark-config__versions-custom';
    custom.innerHTML = `
      <label for="spark-custom-artwork-versions">Number of artworks</label>
      <input id="spark-custom-artwork-versions" data-input="customArtworkVersions" type="number" inputmode="numeric" min="1" step="1" value="${escapeHtml(this.state.artworkVersions || 1)}" placeholder="Enter artwork quantity">
    `;
    customOption.after(custom);
    this.bindOptionEvents(custom);
  }

  renderQuantitySection(title) {
    const isSheet = this.profile.engine === 'sheets';
    const isFixedQuantityEngine = this.isFixedQuantityEngine();
    const allFixedValues = isFixedQuantityEngine ? this.quantityOptions() : [];
    const values = isFixedQuantityEngine
      ? (this.showAllFixedQuantities ? allFixedValues : allFixedValues.slice(0, 9))
      : this.visibleQuantityValues(isSheet);
    const field = this.quantityField();
    const basePrice = this.baseQuantityPrice(values, field);

    return `
      <div class="spark-config__section spark-config__section--quantity">
        <div class="spark-config__section-heading">
          <h3>${escapeHtml(this.selectionTitle(title))}</h3>
        </div>
        <div class="spark-config__quantity-list">
          ${values.map((value) => this.renderQuantityRow(value, field, isSheet, basePrice)).join('')}
          ${isFixedQuantityEngine ? this.renderFixedQuantityDisclosure(allFixedValues.length) : `
            ${this.renderCustomQuantityResultRow(field, isSheet, basePrice)}
            ${this.renderCustomQuantityRow(field, isSheet)}
          `}
        </div>
      </div>
    `;
  }

  renderLaminationSection() {
    const type = this.laminationTypeForMaterial(this.state.material);
    if (!type) return '';
    const selected = this.state.lamination === 'matched' ? 'matched' : 'none';
    return `
      <div class="spark-config__section spark-config__section--lamination">
        <div class="spark-config__section-title-row">
          <span class="spark-config__lamination-title">
            <h3>Protective film</h3>
            <button class="spark-config__help-trigger" type="button" data-lamination-help-open aria-label="More information about lamination">?</button>
          </span>
          <dialog class="spark-config__help-dialog" data-lamination-help-dialog aria-labelledby="spark-lamination-help-title">
            <div class="spark-config__help-dialog-inner">
              <button class="spark-config__help-close" type="button" data-lamination-help-close aria-label="Close lamination information">×</button>
              <h4 id="spark-lamination-help-title">About lamination</h4>
              <p>Most sticker orders do not need lamination. The selected PVC vinyl finish is already durable for everyday use. Choose matched lamination only for harsher use, heavy handling, abrasion, frequent moisture, or longer outdoor exposure.</p>
            </div>
          </dialog>
        </div>
        <div class="spark-config__lamination-choices" role="radiogroup" aria-label="Sticker lamination">
          <button type="button" class="spark-config__lamination-choice${selected === 'none' ? ' is-selected' : ''}" data-choice data-field="lamination" data-value="none" role="radio" aria-checked="${selected === 'none'}">
            <strong>No lamination</strong>
            <small>Standard finish without added film</small>
          </button>
          <button type="button" class="spark-config__lamination-choice${selected === 'matched' ? ' is-selected' : ''}" data-choice data-field="lamination" data-value="matched" role="radio" aria-checked="${selected === 'matched'}">
            <strong>Protective film</strong>
            <small>Adds resistance to wear and handling</small>
          </button>
        </div>
      </div>
    `;
  }

  laminationTypeForMaterial(material) {
    const key = String(material || '').toLowerCase();
    const label = String(this.profile.settings?.materials?.[material]?.label || '').toLowerCase();
    if (key.includes('clear') || label.includes('clear') || key.includes('hangtag') || label.includes('hang tag')) return '';
    if (key.includes('matte') || label.includes('matte')) return 'matte';
    if (key.includes('gloss') || label.includes('gloss') || key === 'vinyl' || label === 'vinyl') return 'gloss';
    return '';
  }

  isFixedQuantityEngine() {
    return this.profile?.engine === 'vista-roll-labels';
  }

  renderFixedQuantityDisclosure(totalQuantityChoices) {
    if (totalQuantityChoices <= 9) return '';
    const expanded = this.showAllFixedQuantities;
    return `
      <button
        type="button"
        class="spark-config__quantity-disclosure"
        data-quantity-disclosure
        aria-expanded="${expanded}"
      >
        <span>${expanded ? 'Show fewer quantities' : 'Show more quantities'}</span>
        <span class="spark-config__quantity-disclosure-icon" aria-hidden="true">${expanded ? '−' : '+'}</span>
      </button>
    `;
  }

  quantityField() {
    return this.profile?.engine === 'sheets' ? 'labelQuantity' : 'quantity';
  }

  visibleQuantityValues(isSheet = this.profile.engine === 'sheets') {
    return isSheet
      ? [50, 100, 200, 300, 500, 1000, 2000, 3000, 5000]
      : this.profile.engine === 'jukebox-magnets'
        ? this.quantityOptions()
        : this.quantityOptions().slice(0, 9);
  }

  baseQuantityPrice(values, field) {
    for (const value of values) {
      const price = this.priceForOverrides({ [field]: Number(value) });
      if (this.quantityUnit(price) > 0) return price;
    }
    return null;
  }

  renderQuantityRow(value, field, isSheet, basePrice = null) {
    const rowPrice = this.priceForOverrides({ [field]: Number(value) });
    const selected = !this.state.customQuantity && Number(this.state[field]) === Number(value);
    return this.renderQuantityButton(value, field, isSheet, rowPrice, basePrice, selected);
  }

  renderCustomQuantityResultRow(field, isSheet, basePrice = null) {
    if (!this.state.customQuantity || Number(this.state[field] || 0) <= 0) return '';
    const value = Number(this.state[field]);
    const rowPrice = this.priceForOverrides({ [field]: value });
    if (!rowPrice) return '';
    return this.renderQuantityButton(value, field, isSheet, rowPrice, basePrice, true, 'data-custom-quantity-result="true"');
  }

  renderQuantityButton(value, field, isSheet, rowPrice, basePrice = null, selected = false, extraAttributes = '') {
    const rowVariantId = this.variantIdForOverrides({ [field]: Number(value) });
    const quantityValues = this.visibleQuantityValues(isSheet);
    const firstQuantity = Number(quantityValues[0] || 0);
    const savings = rowPrice ? this.quantityDiscount(rowPrice, basePrice) : 0;
    const descriptor = Number(value) === firstQuantity ? 'Small-batch order' : '';
    const badge = Number(value) === 100
      ? 'Most popular'
      : Number(value) === 500 ? 'Best value' : '';
    return `
      <button
        type="button"
        class="spark-config__quantity-row${selected ? ' is-selected' : ''}"
        data-choice
        data-field="${escapeHtml(field)}"
        data-value="${escapeHtml(value)}"
        aria-pressed="${selected ? 'true' : 'false'}"
        ${extraAttributes}
      >
        <span class="spark-config__quantity-radio" aria-hidden="true"></span>
        <span class="spark-config__quantity-choice">
          <span class="spark-config__quantity-title-line">
            <span class="spark-config__quantity-label">${this.renderQuantityLabel(value, isSheet, rowPrice)}</span>
            ${badge ? `<span class="spark-config__quantity-badge">${escapeHtml(badge)}</span>` : ''}
          </span>
          ${savings > 0
            ? `<small class="spark-config__quantity-savings">Save ${escapeHtml(savings)}%</small>`
            : descriptor ? `<small>${escapeHtml(descriptor)}</small>` : ''}
        </span>
        <span class="spark-config__quantity-pricing">
          <strong class="spark-config__quantity-total">${rowPrice ? this.moneyMarkup(rowPrice.total, rowVariantId) : '-'}</strong>
        </span>
      </button>
    `;
  }

  renderCustomQuantityRow(field, isSheet) {
    const label = isSheet ? 'Custom Labels Quantity' : 'Custom Quantity';
    return `
      <div class="spark-config__quantity-custom-row is-open">
        <label for="spark-config-custom-quantity-${escapeHtml(field)}">${escapeHtml(label)}</label>
        <input id="spark-config-custom-quantity-${escapeHtml(field)}" data-input="${escapeHtml(field)}" data-custom-quantity-input type="text" inputmode="numeric" pattern="[0-9]*" autocomplete="off" value="${escapeHtml(this.state.customQuantity ? (this.state[field] || '') : '')}" placeholder="${isSheet ? 'Enter label count' : 'Enter quantity'}">
      </div>
    `;
  }

  quantityOptions() {
    if (this.profile.engine === 'vista-roll-labels') return this.profile.settings?.rollLabels?.quantities || [];
    if (this.profile.engine === 'vista-magnets') return this.profile.settings?.magnets?.quantities || [];
    if (this.profile.engine === 'jukebox-magnets') return this.profile.settings?.magnets?.quantities || [];
    return this.profile.settings?.quantityPresets || [50, 100, 200, 300, 500, 1000, 2000, 3000, 5000];
  }

  quantityLabel(value, isSheet, price = null) {
    if (!isSheet) return `${formatNumber(value)} ${this.profile.engine === 'vista-roll-labels' ? 'labels' : 'pcs'}`;
    const sheetFit = price?.sheetFit || this.price?.sheetFit || DEFAULT_LABELS_PER_SHEET;
    const sheets = Math.max(1, Math.ceil(Number(value) / Math.max(1, sheetFit)));
    return `${formatNumber(value)} Labels (${formatNumber(sheets)} Sheets)`;
  }

  renderQuantityLabel(value, isSheet, price = null) {
    if (!isSheet) return escapeHtml(this.quantityLabel(value, false, price));
    const sheetFit = price?.sheetFit || this.price?.sheetFit || DEFAULT_LABELS_PER_SHEET;
    const sheets = Math.max(1, Math.ceil(Number(value) / Math.max(1, sheetFit)));
    return `${formatNumber(value)} Labels <span class="spark-config__quantity-sheets">(${formatNumber(sheets)} Sheets)</span>`;
  }

  consumePriceAnimationState() {
    const currentTotal = this.price ? Number(this.price.total || 0) : null;
    const previousTotal = this.lastRenderedPriceTotal;
    this.lastRenderedPriceTotal = Number.isFinite(currentTotal) ? currentTotal : null;
    return Number.isFinite(previousTotal)
      && Number.isFinite(currentTotal)
      && Math.abs(currentTotal - previousTotal) >= 0.01;
  }

  renderPriceBlock(animate = false) {
    const hasPrice = Boolean(this.price);
    const discount = this.currentDiscount();
    const originalTotal = hasPrice ? this.originalTotalForPrice(this.price, discount) : 0;
    const hasOriginal = hasPrice && Number(originalTotal || 0) > Number(this.price.total || 0) + 0.01;

    const shippingMessage = 'Free Shipping Canada Wide';
    const shippingMarkup = this.country === 'CA'
      ? `<div class="spark-config__shipping" aria-label="${shippingMessage}">
          <span class="spark-config__shipping-icon" aria-hidden="true">
            <svg class="spark-config__shipping-truck" viewBox="0 0 38 28" focusable="false">
              <path class="spark-config__shipping-speed" d="M1 7h5M1 12h5M3 17h3"/>
              <path fill="currentColor" d="M8 4.5A2.5 2.5 0 0 1 10.5 2h14A2.5 2.5 0 0 1 27 4.5V17H8V4.5Z"/>
              <path fill="currentColor" d="M27 8h4.5L37 14v5h-2.6a5.4 5.4 0 0 0-10.8 0H27V8Z"/>
              <path fill="currentColor" d="M8 17h2.1a5.4 5.4 0 0 1 10.8 0h2.7v2H20.9a5.4 5.4 0 0 1-10.8 0H8v-2Z"/>
              <circle cx="15.5" cy="20" r="4" fill="currentColor"/>
              <circle cx="29" cy="20" r="4" fill="currentColor"/>
              <circle cx="15.5" cy="20" r="1.7" fill="#ffe2ca"/>
              <circle cx="29" cy="20" r="1.7" fill="#ffe2ca"/>
            </svg>
          </span>
          <strong>${shippingMessage}</strong>
        </div>`
      : '';

    return `
      <div class="spark-config__price${hasPrice ? ' has-price' : ' is-empty'}${this.country === 'CA' ? ' has-canada-shipping' : ''}" data-price>
        <span>Your price</span>
        <div class="spark-config__price-stack${animate ? ' is-animated' : ''}">
          <strong>${hasPrice ? this.moneyMarkup(this.price.total, this.displayVariantId(), true) : 'Choose options'}</strong>
          ${hasOriginal && this.usesBaseCurrency() ? `<del>${formatMoney(originalTotal, this.currency)}</del>` : ''}
        </div>
        ${shippingMarkup}
      </div>
    `;
  }

  currentDiscount() {
    if (!this.price) return 0;
    const explicit = Math.round(Number(this.price.discount || 0));
    if (explicit > 0) return explicit;
    const originalTotal = Number(this.price.originalTotal || 0);
    const total = Number(this.price.total || 0);
    if (originalTotal > total + 0.01) {
      return Math.max(0, Math.round(((originalTotal - total) / originalTotal) * 100));
    }

    // Per-label quantity comparisons are badges, not former order prices.
    if (this.profile.engine === 'sheets') return 0;
    const isSheet = this.profile.engine === 'sheets';
    const field = isSheet ? 'labelQuantity' : 'quantity';
    const basePrice = this.baseQuantityPrice(this.visibleQuantityValues(isSheet), field);
    return this.quantityDiscount(this.price, basePrice);
  }

  quantityDiscount(price, basePrice) {
    if (this.profile.engine === 'sheets') {
      const labels = Number(price?.labelQuantity || price?.quantity || 0);
      const baseLabels = Number(basePrice?.labelQuantity || basePrice?.quantity || 0);
      const total = Number(price?.total || 0);
      const baseTotal = Number(basePrice?.total || 0);
      if (![labels, baseLabels, total, baseTotal].every((value) => Number.isFinite(value) && value > 0)) return 0;
      const unit = total / labels;
      const baseUnit = baseTotal / baseLabels;
      if (labels <= baseLabels || unit >= baseUnit) return 0;
      const tier = getDiscountFromRows(labels, settingValue(this.profile.settings, 'quantityDiscounts', 'quantity_discounts', []));
      const actualSavings = Math.max(0, Math.floor((1 - unit / baseUnit) * 100 + 1e-9));
      return Math.max(0, Math.min(99, Math.floor(tier), actualSavings));
    }
    const explicit = Math.round(Number(price?.discount || 0));
    if (explicit > 0) return explicit;
    const originalTotal = Number(price?.originalTotal || 0);
    const total = Number(price?.total || 0);
    if (originalTotal > total + 0.01) {
      return Math.max(0, Math.round(((originalTotal - total) / originalTotal) * 100));
    }
    if (!['vista-roll-labels', 'vista-magnets'].includes(this.profile.engine)) return 0;
    // Custom roll-label totals come from the exact VPS/R2 price index. Comparing
    // their unit cost with the smallest preset quantity invents a misleading
    // savings percentage, so only show savings supplied by the pricing row.
    if (this.profile.engine === 'vista-roll-labels' && this.state.customSize) return 0;

    const baseUnit = this.quantityUnit(basePrice);
    const unit = this.quantityUnit(price);
    if (!baseUnit || !unit || unit >= baseUnit) return 0;
    return Math.max(0, Math.round(((baseUnit - unit) / baseUnit) * 100));
  }

  quantityUnit(price) {
    if (!price) return 0;
    const direct = Number(price.each || price.unit || 0);
    if (direct > 0) return direct;
    const quantity = Number(price.quantity || price.labelQuantity || 0);
    return quantity > 0 ? Number(price.total || 0) / quantity : 0;
  }

  originalTotalForPrice(price, discount) {
    const explicit = Number(price?.originalTotal || 0);
    if (explicit > Number(price?.total || 0) + 0.01) return explicit;
    const percent = Number(discount || 0);
    if (!price?.total || percent <= 0 || percent >= 100) return 0;
    return Number(price.total) / (1 - (percent / 100));
  }

  selectionSummaryItems() {
    const items = [];
    const finish = String(this.finishLabel(this.state.finish) || '').trim();
    const meaningfulFinish = !['', 'none', 'no foil', 'no finish'].includes(finish.toLowerCase());
    const quantity = this.profile.engine === 'sheets'
      ? Number(this.price?.labelQuantity || this.state.labelQuantity || this.state.quantity || 0)
      : Number(this.price?.quantity || this.state.quantity || 0);

    if (this.state.shape) items.push({ label: 'Shape', value: this.shapeLabel(this.state.shape) });
    if (this.state.sizeLabel || this.state.size) items.push({ label: 'Size', value: this.state.sizeLabel || this.state.size });
    if (this.state.material) items.push({ label: 'Material', value: this.materialLabel(this.state.material) });
    if (meaningfulFinish) items.push({ label: 'Finish', value: finish });

    const laminationType = this.profile.engine === 'stickers'
      ? this.laminationTypeForMaterial(this.state.material)
      : '';
    if (laminationType) {
      items.push({
        label: 'Lamination',
        value: this.state.lamination === 'matched'
          ? `${laminationType === 'matte' ? 'Matte' : 'Gloss'} Lamination`
          : 'No Lamination',
      });
    }

    if (this.profile.engine === 'vista-roll-labels') {
      const directionLabels = {
        'Right of Copy': 'Right Edge First',
        'Left of Copy': 'Left Edge First',
        'Top of Copy': 'Top Edge First',
        'Bottom of Copy': 'Bottom Edge First',
      };
      items.push({
        label: 'Roll direction',
        value: directionLabels[this.state.unwindDirection] || this.state.unwindDirection || 'Right Edge First',
      });
    }

    if (this.profile.engine === 'sheets') {
      const artworkVersions = Math.max(1, Number(this.price?.artworkVersions || this.state.artworkVersions || 1));
      items.push({
        label: 'Artwork versions',
        value: `${formatNumber(artworkVersions)} ${artworkVersions === 1 ? 'Artwork' : 'Artworks'}`,
      });
    }

    if (quantity > 0) {
      items.push({
        label: 'Quantity',
        value: this.quantityLabel(quantity, this.profile.engine === 'sheets', this.price),
      });
    }

    return items.filter((item) => String(item.value || '').trim());
  }

  renderSelectionSummary() {
    const items = this.selectionSummaryItems();
    if (!items.length) return '';
    const summary = items.map((item) => item.value).join(' · ');

    return `
      <section class="spark-config__selection-summary" aria-label="Order summary">
        <div class="spark-config__selection-summary-head">
          <span>
            <small>Your selected setup</small>
            <strong>${escapeHtml(summary)}</strong>
          </span>
          <button type="button" data-back-to-options>Edit options</button>
        </div>
      </section>
    `;
  }

  renderArtworkTotal() {
    return `
      <div class="spark-config__order-total" aria-live="polite">
        <span>Order total</span>
        <strong>${this.price ? this.moneyMarkup(this.price.total, this.displayVariantId(), true) : 'Choose options'}</strong>
      </div>
    `;
  }

  renderArtworkSection() {
    const requiredCount = this.requiredArtworkFileCount();
    const isSheetUpload = this.profile?.engine === 'sheets';
    const fileWord = requiredCount === 1 ? 'file' : 'files';
    const initialHelp = isSheetUpload
      ? `Select ${requiredCount} ${fileWord}, one for each artwork version. ${ARTWORK_FILE_HELP}.`
      : `${ARTWORK_FILE_HELP}.`;
    const willEmailArtwork = this.state.artworkMode === 'email_later';
    return `
      <div class="spark-config__section spark-config__section--artwork">
        <div class="spark-config__artwork-head">
          <h3>Upload your artwork</h3>
        </div>

        ${this.renderSelectionSummary()}

        <div class="spark-config__artwork-mode" role="group" aria-label="Artwork delivery method">
          <button type="button" class="${willEmailArtwork ? '' : 'is-active'}" data-artwork-mode="upload" aria-pressed="${!willEmailArtwork}">Upload artwork now</button>
          <button type="button" class="${willEmailArtwork ? 'is-active' : ''}" data-artwork-mode="email_later" aria-pressed="${willEmailArtwork}">Email artwork later</button>
          <input data-input="artworkMode" type="checkbox" ${willEmailArtwork ? 'checked' : ''} hidden>
        </div>

        <div class="spark-config__upload-panel" data-artwork-upload-panel${willEmailArtwork ? ' hidden' : ''}>
          <div class="spark-config__upload-grid">
            <div>
              <label class="spark-config__dropzone" data-artwork-dropzone>
                <input data-artwork-file type="file" accept=".pdf,.ai,.eps,.png,.jpg,.jpeg,.tif,.tiff,.svg"${requiredCount > 1 ? ' multiple' : ''} data-required-artwork-files="${requiredCount}" aria-describedby="spark-artwork-file-help">
                <span class="spark-config__dropzone-icon" aria-hidden="true">
                  <svg class="spark-config__upload-arrow" viewBox="0 0 512 512" fill-rule="evenodd" clip-rule="evenodd" focusable="false">
                    <path d="M145.227 0h221.546c39.961 0 76.278 16.334 102.589 42.638C495.666 68.942 512 105.262 512 145.224v221.549c0 39.965-16.334 76.278-42.638 102.589C443.051 495.666 406.738 512 366.773 512H145.227c-39.961 0-76.278-16.334-102.589-42.638C16.334 443.051 0 406.738 0 366.773V145.224c0-39.962 16.334-76.282 42.638-102.586C68.949 16.334 105.266 0 145.227 0zm57.614 225.515c-4.073-.173-6.969-1.531-8.64-4.07-4.539-6.801 1.656-13.522 5.946-18.251 12.196-13.373 42.058-45.523 48.077-52.604 4.556-5.035 11.05-5.035 15.603 0 6.216 7.261 37.588 40.877 49.175 53.883 4.019 4.527 8.993 10.704 4.805 16.972-1.711 2.539-4.571 3.897-8.648 4.07h-24.744v61.154c0 6.53-5.356 11.893-11.89 11.893h-33.043c-6.533 0-11.889-5.352-11.889-11.893v-61.154h-24.752zm-74.783 62.033c-1.362-5.622.932-10.038 4.596-12.657 3.664-2.619 8.895-3.1 13.221 6.916 3.694 8.55 3.873 30.135 12.942 42.607 4.812 4.073 13.928 5.482 31.397 5.482h122.695c16.121 0 24.539-1.546 28.962-5.544 4.264-3.854 5.935-11.396 8.1-22.968 1.269-6.707 2.566-13.738 4.257-20.577 1.372-5.637 5.435-8.518 9.902-9.16 4.467-.642 9.619 1.989 14.307 9.917-4.979 22.999-5.296 41.581-18.452 54.798-8.615 8.096-22.115 11.525-46.52 11.525H196.214c-25.522 0-39.551-3.072-48.573-11.065-13.413-11.888-14.847-34.607-19.583-53.758z"/>
                  </svg>
                </span>
                <span aria-live="polite">
                  <strong data-artwork-file-label>${isSheetUpload ? `Drop or choose ${requiredCount} ${requiredCount === 1 ? 'file' : 'files'}` : 'Drop your artwork here'}</strong>
                  <small class="spark-config__browse-copy">or <span>browse your device</span></small>
                  <small id="spark-artwork-file-help" data-artwork-file-help>${initialHelp}</small>
                  <span class="spark-config__upload-progress" aria-hidden="true"><i></i></span>
                </span>
              </label>
              <ul class="spark-config__artwork-file-list" data-artwork-file-list hidden></ul>
            </div>

            <aside class="spark-config__artwork-trust" aria-label="Artwork reassurance">
              <h4>Your artwork is checked by a person</h4>
              <ul>
                <li><span aria-hidden="true">&check;</span><div><strong>No print-ready file needed</strong><small>Upload the best artwork you have.</small></div></li>
                <li><span aria-hidden="true">&check;</span><div><strong>Human artwork check</strong><small>We prepare the cut line and flag concerns.</small></div></li>
                <li><span aria-hidden="true">&check;</span><div><strong>Proof before printing</strong><small>Nothing prints until you approve the setup.</small></div></li>
              </ul>
            </aside>

          </div>
        </div>

        <div class="spark-config__later-panel" data-artwork-later-panel${willEmailArtwork ? '' : ' hidden'}>
          <span class="spark-config__later-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><path d="M3 6h18v12H3z"/><path d="m3 7 9 6 9-6"/></svg>
          </span>
          <h4>Order now, send the file when ready</h4>
          <p>After checkout, use the secure artwork-upload link in your order confirmation. Production waits until the artwork and proof are approved.</p>
          <strong class="spark-config__later-confirmation"><span aria-hidden="true">&check;</span>Your order can be placed without a file today.</strong>
        </div>

        <div class="spark-config__artwork-notes" data-artwork-notes-wrap>
          <label for="spark-artwork-notes">Artwork notes <span>(optional)</span></label>
          <textarea id="spark-artwork-notes" data-input="artworkNotes" rows="4" aria-label="Artwork details" placeholder="Example: Please add a white border and keep the small text as large as possible.">${escapeHtml(this.state.artworkNotes || '')}</textarea>
        </div>
      </div>
    `;
  }

  updateArtworkFileState() {
    const fileInput = this.querySelector('[data-artwork-file]');
    const fileLabel = this.querySelector('[data-artwork-file-label]');
    const fileHelp = this.querySelector('[data-artwork-file-help]');
    const fileList = this.querySelector('[data-artwork-file-list]');
    const notesWrap = this.querySelector('[data-artwork-notes-wrap]');
    const dropzone = this.querySelector('[data-artwork-dropzone]');
    const emailLabel = this.querySelector('[data-artwork-email-label]');
    const uploadPanel = this.querySelector('[data-artwork-upload-panel]');
    const laterPanel = this.querySelector('[data-artwork-later-panel]');
    const uploadModeButton = this.querySelector('[data-artwork-mode="upload"]');
    const laterModeButton = this.querySelector('[data-artwork-mode="email_later"]');
    const willEmailArtwork = this.state.artworkMode === 'email_later';
    const inputFiles = Array.from(fileInput?.files || []);
    if (inputFiles.length && !willEmailArtwork) {
      this.mergeArtworkFiles(inputFiles);
      if (fileInput) fileInput.value = '';
    }
    const artworkFiles = willEmailArtwork ? [] : (inputFiles.length ? inputFiles : (this.artworkFiles || []));
    const requiredCount = this.requiredArtworkFileCount();
    const isSheetUpload = this.profile?.engine === 'sheets';
    const selectedCount = artworkFiles.length;
    const hasRequiredArtworkFiles = selectedCount === requiredCount;
    const currentSelectionKey = artworkFiles.map((file) => `${file.name}:${file.size}:${file.lastModified}`).join('|');

    if (fileLabel) {
      fileLabel.textContent = hasRequiredArtworkFiles
        ? (isSheetUpload
          ? `${selectedCount} ${selectedCount === 1 ? 'artwork file' : 'artwork files'} selected`
          : 'Artwork selected')
        : (isSheetUpload
          ? `Drop or choose ${requiredCount} ${requiredCount === 1 ? 'file' : 'files'}`
          : 'Drop your artwork here');
    }

    if (fileHelp) {
      fileHelp.textContent = hasRequiredArtworkFiles
        ? (requiredCount === 1
          ? 'Choose another file to replace it.'
          : 'Choose the files again to replace this selection.')
        : (isSheetUpload
          ? `Select ${requiredCount} ${requiredCount === 1 ? 'file' : 'files'}, one for each artwork version. ${ARTWORK_FILE_HELP}.`
          : `${ARTWORK_FILE_HELP}.`);
    }

    if (fileList) {
      fileList.hidden = selectedCount === 0;
      fileList.innerHTML = artworkFiles.map((file, index) => `
        <li>
          ${this.renderArtworkFilePreview(file)}
          <span class="spark-config__artwork-file-copy">
            <strong title="${escapeHtml(file.name)}">${escapeHtml(file.name)}</strong>
            <small>${escapeHtml(this.artworkFileSizeLabel(file))} · Ready to attach to order</small>
          </span>
          <button class="spark-config__artwork-file-remove" type="button" data-remove-artwork-file="${index}" aria-label="Delete ${escapeHtml(file.name)}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5"/></svg>
          </button>
        </li>
      `).join('');
    }

    if (dropzone) {
      if (hasRequiredArtworkFiles && currentSelectionKey !== this.artworkFileSelectionKey) {
        dropzone.classList.remove('is-ready');
        void dropzone.offsetWidth;
      }
      dropzone.classList.toggle('is-ready', hasRequiredArtworkFiles);
      dropzone.classList.toggle('is-incomplete', selectedCount > 0 && !hasRequiredArtworkFiles);
      dropzone.classList.toggle('is-email-later', willEmailArtwork);
    }

    if (emailLabel) emailLabel.classList.toggle('is-active', willEmailArtwork);
    if (uploadPanel) uploadPanel.hidden = willEmailArtwork;
    if (laterPanel) laterPanel.hidden = !willEmailArtwork;
    if (uploadModeButton) {
      uploadModeButton.classList.toggle('is-active', !willEmailArtwork);
      uploadModeButton.setAttribute('aria-pressed', String(!willEmailArtwork));
    }
    if (laterModeButton) {
      laterModeButton.classList.toggle('is-active', willEmailArtwork);
      laterModeButton.setAttribute('aria-pressed', String(willEmailArtwork));
    }

    this.artworkFileSelectionKey = currentSelectionKey;

    if (notesWrap) notesWrap.hidden = false;

    this.updateAddButtonState();
  }

  requiredArtworkFileCount() {
    return this.profile?.engine === 'sheets'
      ? Math.max(1, toInteger(this.state.artworkVersions || 1))
      : 1;
  }

  mergeArtworkFiles(files) {
    const requiredCount = this.requiredArtworkFileCount();
    if (requiredCount === 1) {
      const replacement = files.at(-1);
      (this.artworkFiles || []).forEach((file) => {
        if (file !== replacement) this.releaseArtworkPreviewUrl(file);
      });
      this.artworkFiles = replacement ? [replacement] : [];
      return this.artworkFiles;
    }

    const previousFiles = this.artworkFiles || [];
    const nextFiles = [...previousFiles, ...files].slice(0, requiredCount);
    const retainedFiles = new Set(nextFiles);
    previousFiles.forEach((file) => {
      if (!retainedFiles.has(file)) this.releaseArtworkPreviewUrl(file);
    });
    this.artworkFiles = nextFiles;
    return this.artworkFiles;
  }

  renderArtworkFilePreview(file) {
    const previewUrl = this.artworkPreviewUrl(file);
    if (previewUrl) {
      return `<span class="spark-config__artwork-file-preview"><img src="${escapeHtml(previewUrl)}" alt=""></span>`;
    }

    const extension = String(file?.name || '').split('.').pop()?.toUpperCase() || 'FILE';

    return `
      <span class="spark-config__artwork-file-preview is-document" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M6 2.75h8l4 4V21.25H6z"/><path d="M14 2.75v4h4"/></svg>
        <strong class="spark-config__artwork-file-type">${escapeHtml(extension)}</strong>
      </span>
    `;
  }

  artworkPreviewUrl(file) {
    if (!file || !/\.(?:png|jpe?g|svg)$/i.test(file.name || '') || typeof URL?.createObjectURL !== 'function') return '';
    if (!this.artworkPreviewUrls.has(file)) {
      this.artworkPreviewUrls.set(file, URL.createObjectURL(file));
    }
    return this.artworkPreviewUrls.get(file) || '';
  }

  artworkFileSizeLabel(file) {
    const bytes = Math.max(0, Number(file?.size || 0));
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  releaseArtworkPreviewUrl(file) {
    if (!file || !this.artworkPreviewUrls?.has(file)) return;
    const previewUrl = this.artworkPreviewUrls.get(file);
    if (previewUrl && typeof URL?.revokeObjectURL === 'function') URL.revokeObjectURL(previewUrl);
    this.artworkPreviewUrls.delete(file);
  }

  clearArtworkPreviewUrls() {
    if (!this.artworkPreviewUrls) return;
    this.artworkPreviewUrls.forEach((previewUrl) => {
      if (previewUrl && typeof URL?.revokeObjectURL === 'function') URL.revokeObjectURL(previewUrl);
    });
    this.artworkPreviewUrls.clear();
  }

  clearArtworkFiles() {
    this.clearArtworkPreviewUrls();
    this.artworkFiles = [];
  }

  selectedArtworkFiles() {
    const fileInput = this.querySelector('[data-artwork-file]');
    const inputFiles = Array.from(fileInput?.files || []);
    if (inputFiles.length) {
      this.mergeArtworkFiles(inputFiles);
      if (fileInput) fileInput.value = '';
    }
    return this.state.artworkMode === 'email_later' ? [] : (this.artworkFiles || []);
  }

  artworkSelectionKey(files = this.selectedArtworkFiles()) {
    return files.map((file) => `${file.name}:${file.size}:${file.type}:${file.lastModified}`).join('|');
  }

  invalidateArtworkManifest() {
    this.artworkManifestToken = '';
    this.artworkAccessUrl = '';
    this.artworkManifestSelectionKey = '';
    this.artworkUploadProgress = 0;
  }

  storefrontUploadSessionId() {
    const storageKey = 'spark-storefront-upload-session';
    try {
      const existing = window.sessionStorage.getItem(storageKey);
      if (existing) return existing;
      const generated = `session_${window.crypto.randomUUID().replace(/-/g, '')}`;
      window.sessionStorage.setItem(storageKey, generated);
      return generated;
    } catch (_error) {
      return `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    }
  }

  async ensureArtworkUploaded(files) {
    const context = {stage:'authorization'};
    try { return await this.performArtworkUpload(files, context); }
    catch (error) {
      if (!error.cancelled) {
        const details = {code:'configured_cart_failed', productHandle: this.productHandle, stage:'artwork-upload', uploadStage:context.stage, fileCount:files.length, fileBytes:files.reduce((n,f)=>n+f.size,0), fileTypes:[...new Set(files.map(f=>f.type||'unknown'))].slice(0,5).join(','), error:'Artwork upload failed', errorName:error.name||'Error', httpStatus:Number(error.status)||Number(context.httpStatus)||0, requestAborted:String(error.name==='AbortError'), pagePath:location.pathname};
        try { fetch('https://configurator.sparkstickers.com/api/configurator/cart-failure',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(details),keepalive:true}).catch(()=>{}); } catch {}
      }
      throw error;
    }
  }

  async performArtworkUpload(files, context) {
    const selectionKey = this.artworkSelectionKey(files);
    if (this.artworkManifestToken && this.artworkAccessUrl && this.artworkManifestSelectionKey === selectionKey) return this.artworkManifestToken;

    this.statusMessage = 'Uploading artwork securely... 0%';
    this.statusType = '';
    this.patchStatusUi();

    const authorizationResponse = await this.fetchUploadRequest(`${this.uploadApiBase}/api/proofs/storefront-upload-authorize`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        productHandle: this.productHandle,
        sessionId: this.storefrontUploadSessionId(),
        files: files.map((file) => ({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
        })),
      }),
    });
    context.httpStatus = authorizationResponse.status;
    const authorization = await responseJson(authorizationResponse, 'Could not prepare the artwork upload.');
    if (!authorizationResponse.ok) throw new Error(authorization.error || 'Could not prepare the artwork upload.');
    if (!Array.isArray(authorization.uploads) || authorization.uploads.length !== files.length) {
      throw new Error('The artwork upload could not be prepared. Please try again.');
    }

    const completedBytes = new Array(files.length).fill(0);
    const totalBytes = files.reduce((total, file) => total + file.size, 0);
    const updateProgress = (index, loaded) => {
      completedBytes[index] = Math.min(files[index].size, Number(loaded || 0));
      const uploaded = completedBytes.reduce((total, value) => total + value, 0);
      this.artworkUploadProgress = totalBytes > 0 ? Math.min(100, Math.round((uploaded / totalBytes) * 100)) : 0;
      this.statusMessage = `Uploading artwork securely... ${this.artworkUploadProgress}%`;
      this.patchStatusUi();
    };

    for (let index = 0; index < files.length; index += 1) {
      context.stage = 'direct-transfer';
      context.httpStatus = 0;
      try {
        await this.uploadFileToSignedUrl(files[index], authorization.uploads[index], (loaded) => updateProgress(index, loaded));
      } catch (directError) {
        const upload = authorization.uploads[index];
        if (directError.cancelled || !upload.relayToken) throw directError;
        context.stage = 'relay-transfer';
        context.httpStatus = 0;
        this.statusMessage = 'Reconnecting your secure artwork upload...';
        this.patchStatusUi();
        try {
          const relay = await fetch(`${this.uploadApiBase}/api/proofs/storefront-upload-relay`, {
            method: 'POST', headers: { 'Content-Type': upload.contentType, Authorization: `Bearer ${upload.relayToken}` },
            body: files[index], signal: AbortSignal.timeout(120000),
          });
          context.httpStatus = relay.status;
          if (!relay.ok) throw new Error(`Upload returned HTTP ${relay.status}.`);
        } catch {
          throw new Error('Your artwork could not be transferred. Download the file to your device and select it again, or choose Email artwork later to continue.');
        }
      }
      updateProgress(index, files[index].size);
    }

    context.stage = 'completion';
      context.httpStatus = 0;
    const completionResponse = await this.fetchUploadRequest(`${this.uploadApiBase}/api/proofs/storefront-upload-complete`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ authorizationToken: authorization.authorizationToken }),
    });
    context.httpStatus = completionResponse.status;
    const completion = await responseJson(completionResponse, 'Could not verify the artwork upload.');
    if (!completionResponse.ok || !completion.manifestToken || !completion.artworkAccessUrl) {
      throw new Error(completion.error || 'Could not verify the artwork upload.');
    }
    this.artworkManifestToken = completion.manifestToken;
    this.artworkAccessUrl = completion.artworkAccessUrl;
    this.artworkManifestSelectionKey = selectionKey;
    this.artworkUploadProgress = 100;
    this.statusMessage = 'Artwork uploaded. Adding your custom item to the cart...';
    this.patchStatusUi();
    return this.artworkManifestToken;
  }

  async fetchUploadRequest(url, options) {
    const attempts = 3;
    let lastError;
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        const response = await fetch(url, { ...options, signal: options.signal || AbortSignal.timeout(30000) });
        if (!this.isRetryableUploadStatus(response.status) || attempt === attempts) return response;
        lastError = new Error(`Upload service returned HTTP ${response.status}.`);
      } catch (error) {
        lastError = error;
        if (attempt === attempts) throw error;
      }
      this.statusMessage = `Connection interrupted. Retrying (${attempt + 1} of ${attempts})...`;
      this.patchStatusUi();
      await this.uploadRetryDelay(attempt);
    }
    throw lastError || new Error('The upload service could not be reached.');
  }

  async uploadFileToSignedUrl(file, upload, onProgress) {
    const attempts = 3;
    let lastError;
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        await this.uploadFileAttempt(file, upload, onProgress);
        return;
      } catch (error) {
        lastError = error;
        if (error.cancelled || (!error.transient && !this.isRetryableUploadStatus(error.status)) || attempt === attempts) throw error;
        this.statusMessage = `Connection interrupted. Retrying ${file.name} (${attempt + 1} of ${attempts})...`;
        this.patchStatusUi();
        await this.uploadRetryDelay(attempt);
      }
    }
    throw lastError;
  }

  uploadFileAttempt(file, upload, onProgress) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('PUT', upload.uploadUrl, true);
      request.setRequestHeader('Content-Type', upload.contentType || file.type || 'application/octet-stream');
      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) onProgress(event.loaded);
      });
      request.addEventListener('load', () => {
        if (request.status >= 200 && request.status < 300) {
          resolve();
          return;
        }
        const error = new Error(`Could not upload ${file.name}. Please try again.`);
        error.status = request.status;
        reject(error);
      });
      request.addEventListener('error', () => {
        const error = new Error(`Could not upload ${file.name}. Check your connection and try again.`);
        error.transient = true;
        reject(error);
      });
      request.addEventListener('timeout', () => {
        const error = new Error(`The upload for ${file.name} timed out. Please try again.`);
        error.transient = true;
        reject(error);
      });
      request.addEventListener('abort', () => {
        const error = new Error(`${file.name} upload was cancelled.`);
        error.cancelled = true;
        reject(error);
      });
      request.timeout = 120000;
      request.send(file);
    });
  }

  isRetryableUploadStatus(status) {
    return [408, 425, 429].includes(Number(status)) || Number(status) >= 500;
  }

  uploadRetryDelay(attempt) {
    const delay = 400 * (2 ** (attempt - 1)) + Math.floor(Math.random() * 200);
    return new Promise((resolve) => window.setTimeout(resolve, delay));
  }

  canSubmitArtwork() {
    const artworkFiles = this.selectedArtworkFiles();
    return this.state.artworkMode === 'email_later' || artworkFiles.length === this.requiredArtworkFileCount();
  }

  updateAddButtonState() {
    const addButton = this.querySelector('[data-add-to-cart]');
    if (!addButton) return;
    const disabled = this.loading || !this.canSubmitArtwork();
    addButton.disabled = disabled;
    addButton.setAttribute('aria-disabled', String(disabled));
    addButton.setAttribute('aria-busy', String(this.loading));
    const label = addButton.querySelector('[data-add-to-cart-label]');
    if (label) label.textContent = this.addToCartLabel();
  }

  setupStickyActions() {
    if (this.stickyObserver) {
      this.stickyObserver.disconnect();
      this.stickyObserver = null;
    }
    if (this.stickyScrollController) {
      this.stickyScrollController.abort();
      this.stickyScrollController = null;
    }

    const stickyActions = this.querySelector('[data-sticky-actions]');
    const inlineActions = this.querySelector('.spark-config__actions');
    if (!stickyActions || !inlineActions || !window.matchMedia('(max-width: 749px)').matches) return;

    const stickyStart = this.querySelector('.spark-config__section--size') || this.querySelector('.spark-config__section');
    const footer = document.querySelector('.spark-footer, footer');
    let inlineVisible = isElementInViewport(inlineActions);
    let footerVisible = isElementInViewport(footer);

    const updateReachedOptions = () => {
      if (!stickyStart) {
        this.stickyReachedOptions = window.scrollY > 80;
        return;
      }
      const rect = stickyStart.getBoundingClientRect();
      this.stickyReachedOptions = window.scrollY > 8 && rect.top <= window.innerHeight * 0.86;
    };

    const updateStickyState = (isInlineVisible) => {
      const hideForInput = this.classList.contains('is-input-active') || this.isTextInputFocused();
      footerVisible = isElementInViewport(footer);
      const shouldShow = Boolean(this.price) && this.stickyReachedOptions && !isInlineVisible && !footerVisible && !hideForInput;
      stickyActions.classList.toggle('is-visible', shouldShow);
      stickyActions.setAttribute('aria-hidden', String(!shouldShow));
    };

    this.stickyObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === inlineActions) inlineVisible = Boolean(entry.isIntersecting);
        if (entry.target === footer) footerVisible = Boolean(entry.isIntersecting);
      });
      updateStickyState(inlineVisible);
    }, {
      root: null,
      threshold: 0.08,
    });

    this.stickyScrollController = new AbortController();
    window.addEventListener('scroll', () => {
      updateReachedOptions();
      updateStickyState(inlineVisible);
    }, { passive: true, signal: this.stickyScrollController.signal });

    this.stickyObserver.observe(inlineActions);
    if (footer) this.stickyObserver.observe(footer);
    updateStickyState(inlineVisible);
  }

  updateStickyInputState() {
    const stickyActions = this.querySelector('[data-sticky-actions]');
    if (!stickyActions) return;
    const inputActive = this.classList.contains('is-input-active') || this.isTextInputFocused();
    this.classList.toggle('is-input-active', inputActive);
    if (inputActive || !this.price) {
      stickyActions.classList.remove('is-visible');
      stickyActions.setAttribute('aria-hidden', 'true');
      return;
    }
    const inlineActions = this.querySelector('.spark-config__actions');
    const footer = document.querySelector('.spark-footer, footer');
    if (inlineActions && this.stickyReachedOptions && !isElementInViewport(inlineActions) && !isElementInViewport(footer) && window.matchMedia('(max-width: 749px)').matches) {
      stickyActions.classList.add('is-visible');
      stickyActions.setAttribute('aria-hidden', 'false');
    }
  }

  isTextInputFocused() {
    const active = document.activeElement;
    return Boolean(active && this.contains(active) && isTextEntryControl(active));
  }

  setInputActive(active) {
    this.classList.toggle('is-input-active', Boolean(active));
    this.updateStickyInputState();
  }

  async openCartDrawer(cartPayload, variantId) {
    const sections = cartPayload?.sections || {};
    const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = cartResponse.ok ? await cartResponse.json() : { items: [], item_count: 0 };
    this.updateCartCount(cart.item_count || 0);

    try {
      const { CartLinesUpdateEvent } = await import('@shopify/events');
      const deferredEventPromise = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [
            {
              merchandiseId: String(variantId),
              quantity: 1,
            },
          ],
          promise: deferredEventPromise.promise,
        })
      );

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections,
          items: cart.items || [],
          itemCount: cart.item_count || 0,
          source: 'spark-product-configurator',
          sourceId: this.productHandle,
          didError: false,
        },
      });
    } catch (error) {
      // Section replacement below is the authoritative drawer refresh for the custom Spark header.
    }

    await this.refreshCartDrawer(sections);

    this.loading = false;
    this.statusMessage = '';
    this.statusType = '';
    this.render();
  }

  updateCartCount(itemCount) {
    document.querySelectorAll('.spark-cart-count').forEach((node) => {
      node.textContent = String(itemCount);
      node.classList.toggle('is-empty', Number(itemCount) <= 0);
    });
  }

  async refreshCartDrawer(sections = {}) {
    let cartDrawerHtml = sections['cart-drawer-section'];

    if (!cartDrawerHtml) {
      const sectionResponse = await fetch(`${window.location.pathname}?sections=cart-drawer-section`, {
        headers: { Accept: 'application/json' },
      });
      if (sectionResponse.ok) {
        const sectionPayload = await sectionResponse.json();
        cartDrawerHtml = sectionPayload['cart-drawer-section'];
      }
    }

    if (cartDrawerHtml) {
      const parsed = new DOMParser().parseFromString(cartDrawerHtml, 'text/html');
      const nextSection = parsed.getElementById('shopify-section-cart-drawer-section')
        || parsed.querySelector('.shopify-section')
        || parsed.body.firstElementChild;
      const currentSection = document.getElementById('shopify-section-cart-drawer-section');

      if (nextSection && currentSection) {
        currentSection.replaceWith(document.importNode(nextSection, true));
      }
    }

    await customElements.whenDefined('theme-drawer').catch(() => {});
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const drawer = document.querySelector('theme-drawer#cart-drawer');

    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      window.location.href = '/cart';
    }
  }

  focusArtworkStep(delay = 0, targetMode = 'artwork') {
    window.setTimeout(() => {
      const focusTarget = this.querySelector('[data-artwork-dropzone]') || this.querySelector('.spark-config__section--artwork');
      const target = targetMode === 'page'
        ? (this.querySelector('.spark-config__flow') || this.querySelector('.spark-config--artwork') || this.querySelector('.spark-config__section--artwork') || focusTarget)
        : focusTarget;
      if (!target) return;
      const behavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      const alignTarget = (scrollBehavior) => {
        const headerOffset = document.querySelector('.spark-header-wrap')?.getBoundingClientRect().height || 0;
        const targetRect = target.getBoundingClientRect();
        const topGap = headerOffset + 12;
        const scrollParent = scrollableParent(target);

        if (scrollParent && scrollParent !== document.scrollingElement && scrollParent !== document.documentElement) {
          const parentRect = scrollParent.getBoundingClientRect();
          const top = Math.max(0, scrollParent.scrollTop + targetRect.top - parentRect.top - topGap);
          scrollParent.scrollTo({ top, behavior: scrollBehavior });
        } else {
          const top = Math.max(0, targetRect.top + window.scrollY - topGap);
          window.scrollTo({ top, behavior: scrollBehavior });
        }
      };

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => alignTarget(behavior));
      });
      window.setTimeout(() => alignTarget('auto'), 420);

      if (typeof focusTarget?.focus === 'function') focusTarget.focus({ preventScroll: true });
    }, delay);
  }

  renderChoiceSection(title, field, options) {
    const visibleOptions = field === 'shape' ? this.visibleShapeOptions(options) : options;
    if (!visibleOptions?.length) return '';
    const layoutClass = field === 'shape'
      ? (this.profile?.engine === 'vista-roll-labels' ? ' spark-config__section--roll-shape' : ' spark-config__section--five-shape')
      : '';
    const helper = this.choiceSectionHelper(field);
    return `
      <div class="spark-config__section spark-config__section--${escapeHtml(field)}${layoutClass}">
        <div class="spark-config__section-heading">
          <h3>${escapeHtml(field === 'material' ? 'Material' : this.selectionTitle(title))}</h3>
          ${helper ? `<small>${escapeHtml(helper)}</small>` : ''}
        </div>
        <div class="spark-config__choices">
          ${visibleOptions.map((option) => {
            const selected = String(this.state[field]) === String(option.value);
            const displayLabel = this.choiceDisplayLabel(field, option.value, option.label);
            return `
              <button
                type="button"
                class="spark-config__choice${selected ? ' is-selected' : ''}"
                data-choice
                data-field="${escapeHtml(field)}"
                data-value="${escapeHtml(option.value)}"
              >
                ${field === 'shape'
                  ? shapeIcon(option.value)
                  : field === 'finish'
                    ? this.renderFinishIcon(option.value)
                    : materialSwatch(option.value, option.label)}
                <span class="spark-config__choice-copy">
                  <span>${escapeHtml(displayLabel)}</span>
                </span>
              </button>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  choiceSectionHelper(field) {
    return '';
  }

  choiceDisplayLabel(field, value, label) {
    if (field !== 'material' && field !== 'finish') return label;
    const key = `${value || ''} ${label || ''}`.toLowerCase();
    if (key.includes('matte')) return 'Matte';
    if (key.includes('gloss')) return 'Gloss';
    if (key.includes('clear') || key.includes('transparent')) return 'Clear';
    if (key.includes('holographic')) return 'Holographic';
    const rawLabel = String(label || value || '').replace(/\s{2,}/g, ' ').trim();
    if (/^vinyl$/i.test(rawLabel)) return 'Vinyl';
    return rawLabel.replace(/\bvinyl\b/gi, '').replace(/\s{2,}/g, ' ').trim();
  }

  selectionTitle(title) {
    const label = String(title || '').trim();
    if (!label || /^select\s/i.test(label)) return label;
    return `Select ${label}`;
  }

  orderedRollShapeValues(values = this.profile.settings?.rollLabels?.shapes) {
    const configuredValues = [...new Set(values || [])];
    const dieCutValues = configuredValues.filter((value) => this.rollShapeLabel(value) === 'Die-Cut');
    const remainingValues = configuredValues.filter((value) => this.rollShapeLabel(value) !== 'Die-Cut');
    return [...dieCutValues, ...remainingValues];
  }

  rollShapes() {
    return this.orderedRollShapeValues().map((value) => ({ value, label: this.rollShapeLabel(value) }));
  }

  renderRollDirectionSection() {
    const options = [
      { value: 'Right of Copy', label: 'Right Edge First' },
      { value: 'Left of Copy', label: 'Left Edge First' },
      { value: 'Top of Copy', label: 'Top Edge First' },
      { value: 'Bottom of Copy', label: 'Bottom Edge First' },
    ];
    const selectedValue = options.some((option) => option.value === this.state.unwindDirection)
      ? this.state.unwindDirection
      : options[0].value;
    this.state.unwindDirection = selectedValue;
    return `
      <div class="spark-config__section spark-config__section--select">
        <h3>${escapeHtml(this.selectionTitle('Roll direction'))}</h3>
        <div class="spark-config__roll-direction-grid" role="radiogroup" aria-label="Roll direction">
          ${options.map((option) => `
            <button
              type="button"
              class="spark-config__roll-direction${selectedValue === option.value ? ' is-selected' : ''}"
              data-choice
              data-roll-direction-choice
              data-field="unwindDirection"
              data-value="${escapeHtml(option.value)}"
              role="radio"
              aria-checked="${selectedValue === option.value ? 'true' : 'false'}"
            >
              ${this.renderRollDirectionIcon(option.value)}
              <span>${escapeHtml(option.label)}</span>
            </button>
          `).join('')}
        </div>
      </div>
    `;
  }

  renderRollDirectionIcon(direction) {
    const orientationRotations = {
      'Right of Copy': 0,
      'Left of Copy': 180,
      'Top of Copy': 90,
      'Bottom of Copy': -90,
    };
    const rotation = orientationRotations[direction] ?? orientationRotations['Right of Copy'];
    return `
      <svg class="spark-config__roll-direction-icon" viewBox="0 0 96 74" aria-hidden="true">
        <g class="spark-config__roll-web">
          <path d="M18 12h36v40H18z"/>
          <path d="M18 46h36v6H18z"/>
        </g>
        <g class="spark-config__roll-label-face" transform="rotate(${rotation} 36 31)">
          <rect x="23" y="17" width="26" height="28" rx="1.5"/>
          <path d="M26 20h20v22H26z"/>
          <text x="36" text-anchor="middle" class="spark-config__roll-direction-brand">
            <tspan x="36" y="29">SPARK</tspan>
            <tspan x="36" y="35" class="spark-config__roll-direction-brand-subline">STICKERS</tspan>
          </text>
        </g>
        <g class="spark-config__roll-spool">
          <path d="M14 51v7c0 5 10 8 22 8s22-3 22-8v-7"/>
          <ellipse cx="36" cy="51" rx="22" ry="7"/>
          <ellipse cx="36" cy="51" rx="7" ry="2.8"/>
          <path d="M29 54c2 1 4.3 1.5 7 1.5s5-.5 7-1.5"/>
        </g>
        <g class="spark-config__roll-feed-arrow">
          <path d="M59 31h27m-5-5 5 5-5 5"/>
        </g>
      </svg>
    `;
  }

  rollSizes() {
    const model = this.profile.settings?.rollLabels || {};
    const values = model.compatible_sizes_by_shape?.[this.state.shape] || model.fixed_sizes || [];
    return values.map((value) => ({ value, label: value === 'Custom' ? 'Custom Size' : value.replace('x', ' x ') }));
  }

  rollFinishes(material = this.state.material) {
    const model = this.profile.settings?.rollLabels || {};
    const preferredOrder = ['Matte', 'Gloss', 'Soft Touch'];
    const availableFinishes = new Set(
      (this.localMatrix?.rows || [])
        .filter((row) => row.criteria?.material === material && row.criteria?.finish)
        .map((row) => row.criteria.finish),
    );
    const supportedValues = availableFinishes.size
      ? (model.finishes || []).filter((value) => availableFinishes.has(value))
      : (model.finishes || []);
    const values = [
      ...preferredOrder.filter((value) => supportedValues.includes(value)),
      ...supportedValues.filter((value) => !preferredOrder.includes(value)),
    ];
    return values.map((value) => ({ value, label: value }));
  }

  renderRollFinishSection() {
    return this.renderChoiceSection('Finish', 'finish', this.rollFinishes());
  }

  renderFinishIcon(finish) {
    const key = String(finish || '').toLowerCase().replace(/\s+/g, '-');
    if (key === 'soft-touch') {
      return '<span class="spark-config__finish-swatch spark-config__finish-swatch--soft-touch" aria-hidden="true"><svg viewBox="0 0 28 28"><path d="M4 9c4-4 8-4 12 0s6 4 8 1M4 14c4-4 8-4 12 0s6 4 8 1M4 19c4-4 8-4 12 0s6 4 8 1"/></svg></span>';
    }
    if (key === 'matte') return '<span class="spark-config__finish-swatch spark-config__finish-swatch--matte" aria-hidden="true"></span>';
    return '<span class="spark-config__finish-swatch spark-config__finish-swatch--gloss" aria-hidden="true"></span>';
  }

  magnetShapes() {
    return (this.profile.settings?.magnets?.shapes || []).map((value) => ({ value, label: value }));
  }

  magnetSizes() {
    const model = this.profile.settings?.magnets || {};
    const values = model.compatible_sizes_by_shape?.[this.state.shape] || model.sizes || [];
    return values.map((value) => ({ value, label: value }));
  }

  magnetOrientations() {
    const model = this.profile.settings?.magnets || {};
    const values = model.compatible_orientations_by_shape?.[this.state.shape] || model.orientations || [];
    return values.map((value) => ({ value, label: value }));
  }

  magnetFinishes() {
    const model = this.profile.settings?.magnets || {};
    const compatible = model.compatibility?.[this.state.shape]?.[this.state.size]?.[this.state.orientation];
    const values = compatible || model.finishes || [];
    return values.map((value) => ({ value, label: value }));
  }

  materialLabel(value) {
    const option = this.materialOptions().find((item) => item.value === value);
    return this.choiceDisplayLabel('material', value, option?.label || value);
  }

  finishLabel(value) {
    const source = this.profile.settings?.sheetFinishes || {};
    return this.choiceDisplayLabel('finish', value, source[value]?.label || value);
  }

  shapeLabel(value) {
    if (this.profile.engine === 'vista-roll-labels') {
      return this.rollShapeLabel(value);
    }

    return labelFor(value, this.profile.settings?.shapes);
  }

  rollShapeLabel(value) {
    return value === 'Custom' ? 'Die-Cut' : value;
  }
}

function shapeIcon(value) {
  const key = String(value || '').toLowerCase().replace(/[^a-z]/g, '');
  const shapes = {
    circle: 'circle',
    square: 'square',
    rectangle: 'rectangle',
    rounded: 'rounded',
    roundedrectangle: 'rounded-rectangle',
    roundedsquare: 'rounded',
    oval: 'oval',
    diecut: 'diecut',
    custom: 'diecut',
  };
  const icon = shapes[key] || shapes.diecut;
  return `<svg class="spark-config__shape-icon" viewBox="0 0 64 64" aria-hidden="true" focusable="false"><use href="#spark-shape-icon-${icon}"></use></svg>`;
}

function materialSwatch(value, label) {
  const lower = `${value} ${label}`.toLowerCase();
  if (!/(vinyl|clear|holographic|foil|paper|matte|gloss|plastic|silver|gold)/.test(lower)) return '';
  return `<i class="spark-config__swatch spark-config__swatch--${swatchClass(lower)}" aria-hidden="true"></i>`;
}

function swatchClass(value) {
  if (value.includes('holographic')) return 'holographic';
  if (value.includes('gold')) return 'gold';
  if (value.includes('silver')) return 'silver';
  if (value.includes('clear')) return 'clear';
  if (value.includes('gloss')) return 'gloss';
  if (value.includes('plastic') || value.includes('vinyl')) return 'vinyl';
  return 'paper';
}

function labelFor(value, map) {
  return map?.[value] || value;
}

function parseSize(value) {
  const match = String(value || '').match(/([\d.]+)\s*"?\s*x\s*([\d.]+)/i);
  return {
    width: match ? Number(match[1]) : 0,
    height: match ? Number(match[2]) : 0,
  };
}

function normalizeSizeKey(value) {
  const size = parseSize(value);
  if (!size.width || !size.height) return String(value || '').replace(/\s+/g, '').toLowerCase();
  return `${formatSizeNumber(size.width)}x${formatSizeNumber(size.height)}`;
}

function formatSizeNumber(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';
  return Number.isInteger(number) ? String(number) : String(Number(number.toFixed(2))).replace(/\.?0+$/, '');
}

function formatSizeLabel(width, height, spaced = true) {
  const joiner = spaced ? '" x ' : '"x';
  return `${formatSizeNumber(width)}${joiner}${formatSizeNumber(height)}"`;
}

function sizesMatch(expected, actual) {
  if (String(actual ?? '') === String(expected ?? '')) return true;
  const expectedSize = parseSize(expected);
  const actualSize = parseSize(actual);
  if (!expectedSize.width || !expectedSize.height || !actualSize.width || !actualSize.height) return false;
  const exact = Number(expectedSize.width) === Number(actualSize.width) && Number(expectedSize.height) === Number(actualSize.height);
  const swapped = Number(expectedSize.width) === Number(actualSize.height) && Number(expectedSize.height) === Number(actualSize.width);
  return exact || swapped;
}

function toNumber(value) {
  const number = Number.parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(number) ? number : 0;
}

function toInteger(value) {
  const number = Number.parseInt(String(value ?? '').replace(/[^\d]/g, ''), 10);
  return Number.isFinite(number) ? number : 0;
}

function numberValue(value, fallback = 0) {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? number : fallback;
}

function roundMoney(value) {
  const number = Number.parseFloat(value);
  return Number.isFinite(number) ? Math.round((number + Number.EPSILON) * 100) / 100 : 0;
}

function configuredVariantLabel(price) {
  return `Made to order - $${Number(price).toFixed(2)}`;
}

function stablePayloadKey(payload) {
  return Object.keys(payload || {})
    .sort()
    .map((key) => `${key}:${String(payload[key])}`)
    .join('|');
}

function rollLabelCustomIndexKey(widthIndex, heightIndex, materialIndex, finishIndex, quantityIndex) {
  return [widthIndex, heightIndex, materialIndex, finishIndex, quantityIndex].join('|');
}

function indexValueIndex(values = [], value) {
  return values.indexOf(value);
}

function settingValue(settings, camelKey, snakeKey, fallback = undefined) {
  return settings?.[camelKey] ?? settings?.[snakeKey] ?? fallback;
}

function getDiscountFromRows(quantity, rows) {
  let discount = 0;
  (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      min: toInteger(row.min ?? row.minimum ?? row.min_quantity ?? row.minQuantity),
      discount: numberValue(row.discount, 0),
    }))
    .filter((row) => row.min > 0)
    .sort((a, b) => a.min - b.min)
    .forEach((row) => {
      if (quantity >= row.min) discount = row.discount;
    });
  return discount;
}

function rollLabelDiscount(settings, quantity) {
  return getDiscountFromRows(quantity, settingValue(settings, 'rollLabelDiscountLadder', 'roll_label_discount_ladder', []));
}

function priceProfileMultiplier(settings, priceProfile = '') {
  const markups = settingValue(settings, 'priceProfileMarkups', 'price_profile_markups', {});
  const profileMarkup = numberValue(markups?.[priceProfile], 0);
  return 1 + profileMarkup / 100;
}

function preciseReferenceNumber(value) {
  return Number(Number(value).toFixed(10));
}

function calculateReferencePrice(settings, payload) {
  const reference = settingValue(settings, 'referencePricing', 'reference_pricing');
  const width = Math.max(0, numberValue(payload.width, 0));
  const height = Math.max(0, numberValue(payload.height, 0));
  const quantity = Math.max(0, toInteger(payload.quantity));
  const category = String(payload.category || '');
  const material = String(payload.material || '');
  const curve = reference?.materialCurves?.[material] || reference?.curve;
  const areaBands = Array.isArray(curve?.areaBands) ? curve.areaBands : [];
  if (!reference || reference.category !== category || !width || !height || !quantity || !areaBands.length) return null;

  const area = preciseReferenceNumber(width * height);
  const areaBand = areaBands.find((band) => area <= numberValue(band.maxArea, 0)) || areaBands.at(-1);
  const quantityBands = Array.isArray(areaBand?.quantityBands) ? areaBand.quantityBands : [];
  const quantityBand = quantityBands.find((band) => quantity <= toInteger(band.maxQuantity)) || quantityBands.at(-1);
  if (!quantityBand) return null;

  const baseRate = numberValue(curve.baseRate, 0);
  let referenceTotal = preciseReferenceNumber(area * baseRate * quantity * numberValue(quantityBand.factor, 0));
  const areaIndex = areaBands.indexOf(areaBand);
  const lastAreaIndex = payload.sizeMonotonic === true ? areaBands.length - 1 : areaIndex;
  for (let index = areaIndex; index <= lastAreaIndex; index += 1) {
    // Match the server's lower envelope over both size and quantity bands.
    const candidateArea = index === areaIndex ? area : numberValue(areaBands[index - 1].maxArea, 0);
    if (index !== areaIndex && candidateArea >= (payload.maximumArea ?? Infinity)) break;
    const bands = areaBands[index].quantityBands;
    const selected = bands.find((band) => quantity <= toInteger(band.maxQuantity)) || bands.at(-1);
    const selectedIndex = bands.indexOf(selected);
    for (let quantityIndex = selectedIndex; quantityIndex < bands.length; quantityIndex += 1) {
      const candidateQuantity = quantityIndex === selectedIndex
        ? quantity : toInteger(bands[quantityIndex - 1].maxQuantity) + 1;
      referenceTotal = Math.min(referenceTotal, preciseReferenceNumber(
        candidateArea * baseRate * candidateQuantity * numberValue(bands[quantityIndex].factor, 0),
      ));
    }
  }

  const materialMultiplier = Math.max(0.000001, numberValue(reference.materialMultipliers?.[material], 1));
  const sourceAdjustmentPercent = Math.min(99.999999, Math.max(0, numberValue(reference.sourceAdjustmentPercent, 0)));
  const sourceMaterialMultiplier = Math.max(0.000001, numberValue(
    category === 'sheets'
      ? reference.sourceMaterialMultipliers?.vinyl
      : reference.sourceMaterialMultipliers?.[material],
    1,
  ));
  const adjustmentPercent = Math.min(99.999999, Math.max(0, numberValue(reference.adjustmentPercent, 0)));
  const additionalMultiplier = Math.max(0.000001, numberValue(payload.additionalMultiplier, 1));
  const minimum = Math.max(0, numberValue(payload.minimum, numberValue(settingValue(settings, 'minimumPrice', 'minimum_price'), 30)));
  const sourceTotal = sourceAdjustmentPercent > 0 || sourceMaterialMultiplier !== 1
    ? roundMoney(referenceTotal * (1 - sourceAdjustmentPercent / 100) * sourceMaterialMultiplier)
    : referenceTotal;
  const vinylMaterialMultiplier = Math.max(0.000001, numberValue(reference.materialMultipliers?.vinyl, 1));
  const directCalculatedTotal = roundMoney(
    sourceTotal * (1 - adjustmentPercent / 100) * materialMultiplier * additionalMultiplier,
  );
  const vinylCalculatedTotal = roundMoney(
    sourceTotal * (1 - adjustmentPercent / 100) * vinylMaterialMultiplier * additionalMultiplier,
  );
  const calculatedTotal = category === 'sheets' && (material === 'clear' || material === 'holographic')
    ? roundMoney(vinylCalculatedTotal * (materialMultiplier / vinylMaterialMultiplier))
    : directCalculatedTotal;
  const total = roundMoney(Math.max(
    minimum,
    calculatedTotal,
  ));

  return {
    total,
    unit: roundMoney(total / quantity),
    each: total / quantity,
    quantity,
    material,
    category,
    pricingModel: 'reference_bands',
    referenceVersion: reference.version,
    referenceCurve: reference?.materialCurves?.[material] ? material : 'default',
    referenceTotal,
    sourceTotal,
    sourceAdjustmentPercent,
    sourceMaterialMultiplier,
    categoryAdjustmentPercent: adjustmentPercent,
    materialMultiplier,
    additionalMultiplier,
  };
}

function stickerLaminationType(settings, material) {
  const materialSettings = settingValue(settings, 'materials', 'materials', {})?.[material] || {};
  const key = String(material || '').toLowerCase();
  const label = String(materialSettings.label || '').toLowerCase();
  if (key.includes('clear') || label.includes('clear') || key.includes('hangtag') || label.includes('hang tag')) return '';
  if (key.includes('matte') || label.includes('matte')) return 'matte';
  if (key.includes('gloss') || label.includes('gloss') || key === 'vinyl' || label === 'vinyl') return 'gloss';
  return '';
}

function calculateJukeboxMagnetPrice(settings, payload) {
  const pricing = settingValue(settings, 'magnetPricing', 'magnet_pricing');
  const options = settingValue(settings, 'magnets', 'magnets', {});
  const customSize = options?.custom_size || options?.customSize || {};
  const width = Math.max(0, numberValue(payload.width, 0));
  const height = Math.max(0, numberValue(payload.height, 0));
  const quantity = Math.max(0, toInteger(payload.quantity));
  const quantities = Array.isArray(options?.quantities) ? options.quantities.map(toInteger) : [];
  const minWidth = numberValue(customSize.min_width ?? customSize.minWidth, 1);
  const minHeight = numberValue(customSize.min_height ?? customSize.minHeight, 1);
  const maxWidth = numberValue(customSize.max_width ?? customSize.maxWidth, 52);
  const maxHeight = numberValue(customSize.max_height ?? customSize.maxHeight, 72);

  if (!pricing || width < minWidth || height < minHeight || width > maxWidth || height > maxHeight) return null;
  const minimumQuantity = Math.min(...quantities.filter((value) => value > 0), 25);
  if (quantity < minimumQuantity) return null;

  const sizeLabel = payload.sizeLabel && payload.sizeLabel !== 'Custom'
    ? String(payload.sizeLabel)
    : formatSizeLabel(width, height, true);
  const presetTotal = numberValue(pricing.basePrices?.[sizeLabel]?.[String(quantity)], 0);
  const sourceTotal = presetTotal > 0
    ? presetTotal
    : calculateJukeboxCustomSourceTotal(pricing, width, height, quantity, pricing.basePrices?.[sizeLabel]);

  if (!sourceTotal || sourceTotal > numberValue(pricing.maximumSourceTotal, 200000)) return null;

  const markupPercent = numberValue(pricing.markupPercent, 50);
  const minimum = numberValue(settingValue(settings, 'minimumPrice', 'minimum_price'), 30);
  const total = roundMoney(Math.max(minimum, sourceTotal * (1 + markupPercent / 100)));

  return {
    total,
    unit: roundMoney(total / quantity),
    each: total / quantity,
    discount: 0,
    originalTotal: total,
    quantity,
    shape: String(payload.shape || 'diecut'),
    width,
    height,
    sizeLabel,
    sourceTotal,
    markupPercent,
    pricingModel: presetTotal > 0
      ? 'jukebox_exact_table'
      : (pricing.basePrices?.[sizeLabel] ? 'jukebox_interpolated_quantity' : 'jukebox_calibrated_custom_size'),
    sourceProvider: pricing.sourceProvider || 'Jukebox Print',
    key: [String(payload.shape || 'diecut'), sizeLabel, quantity].join('|'),
  };
}

function calculateJukeboxCustomSourceTotal(pricing, width, height, quantity, presetRow = null) {
  const presetTotal = interpolateMagnetPriceRow(presetRow, quantity);
  if (presetTotal !== null) return presetTotal;

  const curve = interpolateMagnetCurve(pricing.customPriceCurves, quantity);
  if (!curve) return 0;

  return Math.round(
    numberValue(curve.setup, 0)
    + numberValue(curve.area, 0) * width * height
    + numberValue(curve.dimension_sum ?? curve.dimensionSum, 0) * (width + height),
  );
}

function interpolateMagnetPriceRow(row, quantity) {
  const points = Object.entries(row || {})
    .map(([key, value]) => ({ quantity: toInteger(key), total: numberValue(value, 0) }))
    .filter((point) => point.quantity > 0 && point.total > 0)
    .sort((a, b) => a.quantity - b.quantity);

  if (!points.length) return null;
  return interpolateMagnetNumber(points, quantity, 'total');
}

function interpolateMagnetCurve(curves, quantity) {
  const points = Object.entries(curves || {})
    .map(([key, value]) => ({
      quantity: toInteger(key),
      setup: numberValue(value?.setup, 0),
      area: numberValue(value?.area, 0),
      dimension_sum: numberValue(value?.dimension_sum ?? value?.dimensionSum, 0),
    }))
    .filter((point) => point.quantity > 0)
    .sort((a, b) => a.quantity - b.quantity);

  if (!points.length) return null;
  if (points.length === 1) return points[0];

  const upperIndex = points.findIndex((point) => point.quantity >= quantity);
  const upper = upperIndex >= 0 ? points[upperIndex] : points[points.length - 1];
  const lower = upperIndex > 0
    ? points[upperIndex - 1]
    : (upperIndex === 0 ? points[0] : points[points.length - 2]);
  if (upper.quantity === lower.quantity) return upper;

  const ratio = (quantity - lower.quantity) / (upper.quantity - lower.quantity);
  return {
    setup: interpolateMagnetValue(lower.setup, upper.setup, ratio),
    area: interpolateMagnetValue(lower.area, upper.area, ratio),
    dimension_sum: interpolateMagnetValue(lower.dimension_sum, upper.dimension_sum, ratio),
  };
}

function interpolateMagnetNumber(points, quantity, field) {
  if (points.length === 1) return points[0][field];
  const upperIndex = points.findIndex((point) => point.quantity >= quantity);
  const upper = upperIndex >= 0 ? points[upperIndex] : points[points.length - 1];
  const lower = upperIndex > 0
    ? points[upperIndex - 1]
    : (upperIndex === 0 ? points[0] : points[points.length - 2]);
  if (upper.quantity === lower.quantity) return lower[field];
  const ratio = (quantity - lower.quantity) / (upper.quantity - lower.quantity);
  return Math.round(interpolateMagnetValue(lower[field], upper[field], ratio));
}

function interpolateMagnetValue(lower, upper, ratio) {
  return lower + (upper - lower) * ratio;
}

function stickerLaminationMultiplier(settings, lamination, material) {
  if (lamination !== 'matched' || !stickerLaminationType(settings, material)) return 1;
  const markup = numberValue(settingValue(settings, 'laminationMarkup', 'lamination_markup'), 0);
  return 1 + Math.max(0, markup) / 100;
}

function calculateStickerPrice(settings, payload, profile) {
  const width = Math.max(0, numberValue(payload.width, 0));
  const height = Math.max(0, numberValue(payload.height, 0));
  const quantity = Math.max(0, toInteger(payload.quantity));
  const priceProfile = profile?.priceProfile || payload.priceProfile || payload.price_profile || '';
  const maximumSide = priceProfile === 'hang-tags' ? 12 : 52;
  if (width < 0.5 || height < 0.5 || !quantity || width > maximumSide || height > maximumSide) return null;
  const profiles = settingValue(settings, 'priceProfiles', 'price_profiles', {});
  const profileSettings = profiles?.[priceProfile] || {};
  const minimum = numberValue(profileSettings.minimum_price ?? profileSettings.minimumPrice, numberValue(settingValue(settings, 'minimumPrice', 'minimum_price'), 30));
  const material = payload.material || profile?.defaultMaterial || 'vinylmatte';
  const category = priceProfile === 'hang-tags' ? 'hang_tags' : 'stickers';
  const referencePrice = calculateReferencePrice(settings, {
    category,
    width,
    height,
    quantity,
    material,
    minimum,
    additionalMultiplier: priceProfileMultiplier(settings, priceProfile)
      * stickerLaminationMultiplier(settings, payload.lamination || 'none', material),
    sizeMonotonic: category === 'stickers',
    maximumArea: 52 * 52,
  });
  if (!referencePrice) return null;

  const total = referencePrice.total;
  const discount = getDiscountFromRows(quantity, settingValue(settings, 'quantityDiscounts', 'quantity_discounts', []));
  const originalTotal = discount > 0 && discount < 100 ? roundMoney(total / (1 - discount / 100)) : total;

  return {
    ...referencePrice,
    total,
    unit: roundMoney(total / quantity),
    each: total / quantity,
    discount,
    originalTotal,
    quantity,
    material,
    sizeLabel: payload.sizeLabel || formatSizeLabel(width, height, true),
    priceProfile,
  };
}

function calculateSheetGridFit(stickerWidth, stickerHeight, sheetWidth, sheetHeight, gap, margin) {
  const safeGap = Math.max(0, numberValue(gap, SHEET_LABEL_GAP_INCHES));
  const safeMargin = Math.max(0, numberValue(margin, SHEET_SAFE_MARGIN_INCHES));
  const usableWidth = Math.max(0, numberValue(sheetWidth, 8.5) - (safeMargin * 2));
  const usableHeight = Math.max(0, numberValue(sheetHeight, 11) - (safeMargin * 2));
  const columns = Math.floor((usableWidth + safeGap) / (Math.max(0.01, numberValue(stickerWidth, 0)) + safeGap));
  const rows = Math.floor((usableHeight + safeGap) / (Math.max(0.01, numberValue(stickerHeight, 0)) + safeGap));
  return Math.max(0, columns * rows);
}

function calculateSheetFit(
  width,
  height,
  sheetWidth = 8.5,
  sheetHeight = 11,
  gap = SHEET_LABEL_GAP_INCHES,
  margin = SHEET_SAFE_MARGIN_INCHES,
) {
  return Math.max(
    calculateSheetGridFit(width, height, sheetWidth, sheetHeight, gap, margin),
    calculateSheetGridFit(height, width, sheetWidth, sheetHeight, gap, margin),
  );
}

function sheetDisplayDiscount(settings, sheetQuantity, configuredDiscount) {
  const rows = settingValue(settings, 'sheetDiscountLadder', 'sheet_discount_ladder', []);
  let discount = 0;
  let matched = false;

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const minSheets = toInteger(row.min_sheets ?? row.minSheets ?? row.min);
    if (minSheets > 0 && sheetQuantity >= minSheets) {
      discount = numberValue(row.discount, 0);
      matched = true;
    }
  });

  return matched ? discount : numberValue(configuredDiscount, 0);
}

function isSheetFinishAvailableForMaterial(material, finish) {
  if (['none', 'goldfoil', 'silverfoil'].includes(finish)) return true;
  if (material === 'paper' || material === 'vinyl') return finish === 'matte' || finish === 'gloss';
  if (material === 'clear' || material === 'holographic') return finish === 'gloss';
  return true;
}

function normalizeSheetFinishValue(material, finish, profile) {
  const available = Array.isArray(profile?.finishes) ? profile.finishes : [];
  const candidates = [finish, profile?.defaultFinish, ...available, material === 'clear' || material === 'holographic' ? 'gloss' : 'matte', 'none'];
  return candidates.find((candidate) => (
    candidate
    && (!available.length || available.includes(candidate))
    && isSheetFinishAvailableForMaterial(material, candidate)
  )) || 'none';
}

function calculateSheetPrice(settings, payload, profile) {
  const width = Math.max(0, numberValue(payload.width, 0));
  const height = Math.max(0, numberValue(payload.height, 0));
  const labelQuantity = Math.max(1, toInteger(payload.labelQuantity ?? payload.quantity));
  if (width < 0.5 || height < 0.5 || !labelQuantity || Math.max(width, height) > 18 || Math.min(width, height) > 12) return null;

  const sheetFit = Math.max(1, calculateSheetFit(
    width,
    height,
    payload.sheetWidth ?? payload.sheet_width ?? 8.5,
    payload.sheetHeight ?? payload.sheet_height ?? 11,
    payload.sheetGap ?? payload.sheet_gap ?? SHEET_LABEL_GAP_INCHES,
    payload.sheetMargin ?? payload.sheet_margin ?? SHEET_SAFE_MARGIN_INCHES,
  ));
  const sheetQuantity = Math.max(1, Math.ceil(labelQuantity / sheetFit));
  const pricingSheetFit = Math.max(1, calculateSheetFit(
    width,
    height,
    payload.sheetWidth ?? payload.sheet_width ?? 8.5,
    payload.sheetHeight ?? payload.sheet_height ?? 11,
    LEGACY_PRICING_SHEET_GAP_INCHES,
    0,
  ));
  const pricingSheetQuantity = Math.max(1, Math.ceil(labelQuantity / pricingSheetFit));
  const material = payload.material || profile?.defaultMaterial || 'paper';
  const finish = normalizeSheetFinishValue(material, payload.finish || profile?.defaultFinish || '', profile);
  const matrices = settingValue(settings, 'sheetPriceMatrix', 'sheet_price_matrix', {});
  const fallbackBase = settingValue(settings, 'sheetBasePrices', 'sheet_base_prices', {});
  const rows = matrices?.[material] || matrices?.paper || matrices?.default || fallbackBase || {};
  const finishes = settingValue(settings, 'sheetFinishes', 'sheet_finishes', {});
  const finishMultiplier = Math.max(0.001, numberValue(finishes?.[finish]?.multiplier, 1));
  const versionExtra = numberValue(settingValue(settings, 'sheetVersionExtraPercent', 'sheet_version_extra_percent'), 10);
  const artworkVersions = Math.max(1, toInteger(payload.artworkVersions ?? payload.artwork_versions ?? 1));
  const reference = settingValue(settings, 'referencePricing', 'reference_pricing');
  const referenceSheet = reference?.referenceSheet || {};
  const referenceWidth = Math.max(0.01, numberValue(referenceSheet.width, 2));
  const referenceHeight = Math.max(0.01, numberValue(referenceSheet.height, 2));
  const referenceLabelsPerSheet = Math.max(1, toInteger(referenceSheet.labelsPerSheet || 20));
  const referenceQuantity = pricingSheetQuantity * referenceLabelsPerSheet;
  const additionalMultiplier = finishMultiplier * (1 + (artworkVersions - 1) * (versionExtra / 100));
  const referencePrice = calculateReferencePrice(settings, {
    category: 'sheets',
    width: referenceWidth,
    height: referenceHeight,
    quantity: referenceQuantity,
    material,
    additionalMultiplier,
  });
  if (!referencePrice) return null;

  // Keep the approved matching-size ceiling independent of physical sheet layout.
  const matchingReferencePrice = calculateReferencePrice(settings, {
    category: 'sheets', width, height, quantity: labelQuantity, material, additionalMultiplier,
  });
  if (!matchingReferencePrice) return null;
  const finalTotal = Math.min(referencePrice.total, matchingReferencePrice.total);
  const displayDiscount = sheetDisplayDiscount(settings, pricingSheetQuantity, numberValue(rows?.[pricingSheetQuantity]?.discount, 0));
  const originalTotal = displayDiscount > 0 && displayDiscount < 100 ? roundMoney(finalTotal / (1 - displayDiscount / 100)) : finalTotal;

  return {
    ...referencePrice,
    total: finalTotal,
    unit: roundMoney(finalTotal / labelQuantity),
    each: finalTotal / labelQuantity,
    discount: displayDiscount,
    originalTotal,
    quantity: labelQuantity,
    labelQuantity,
    sheetQuantity,
    sheetFit,
    pricingSheetQuantity,
    pricingSheetFit,
    material,
    finish,
    artworkVersions,
    pricingModel: 'sheet_count_with_matching_sticker_ceiling',
    physicalSheetTotal: referencePrice.total,
    matchingSheetCeilingTotal: matchingReferencePrice.total,
    ceilingApplied: matchingReferencePrice.total < referencePrice.total,
    referenceQuantity,
    referenceSheetQuantity: pricingSheetQuantity,
    referenceSheetWidth: referenceWidth,
    referenceSheetHeight: referenceHeight,
    referenceLabelsPerSheet,
    sourceStickerTotal: referencePrice.sourceTotal,
  };
}

function formatMoney(value, currency) {
  const normalizedCurrency = String(currency || 'CAD').toUpperCase();
  const formatter = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: normalizedCurrency,
  });
  const parts = formatter.formatToParts(Number(value || 0));
  return parts.map((part) => (
    part.type === 'currency' && normalizedCurrency === 'CAD' ? 'CA$' : part.value
  )).join('');
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-CA').format(Number(value || 0));
}

function isAllowedArtworkFile(filename) {
  return /\.(jpe?g|png|pdf|ai|eps|tiff?|svg)$/i.test(String(filename || ''));
}

function customerSafeError(message, fallback) {
  const text = String(message || '').trim();
  if (!text) return fallback;
  if (/Command failed|shopify store execute|stored store auth|No stored app authentication|query-file|variable-file|\.graphql|\.json|npx shopify/i.test(text)) {
    return 'Custom checkout pricing is temporarily unavailable. Please choose a preset quantity or contact us for help.';
  }
  return text;
}

function isTransientConfiguredVariantError(message) {
  return /sold out|not available|unavailable|cannot find variant|variant.*not found/i.test(String(message || ''));
}

function sleep(milliseconds) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}

function isTextEntryControl(element) {
  if (!element || !['INPUT', 'TEXTAREA'].includes(element.tagName)) return false;
  if (element.tagName === 'TEXTAREA') return true;
  return !['checkbox', 'radio', 'file', 'button', 'submit', 'reset', 'hidden'].includes(element.type);
}

function scrollableParent(element) {
  let current = element.parentElement;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const canScroll = /(auto|scroll)/.test(style.overflowY) && current.scrollHeight > current.clientHeight;
    if (canScroll) return current;
    current = current.parentElement;
  }
  return document.scrollingElement || document.documentElement;
}

function isElementInViewport(element) {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
}

async function responseJson(response, fallbackMessage) {
  const text = await response.text();
  if (!text) {
    if (!response.ok) throw new Error(fallbackMessage);
    return {};
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(fallbackMessage);
  }
}

function criteriaMatch(criteria, payload) {
  return Object.entries(criteria).every(([key, expected]) => {
    const actual = payload[key];
    if (typeof expected === 'number') {
      return Number(actual) === expected;
    }

    if ((key === 'size' || key === 'sizeLabel') && sizesMatch(expected, actual)) {
      return true;
    }

    return String(actual ?? '') === String(expected ?? '');
  });
}

function normalizePricingMatrix(matrix) {
  if (!matrix || matrix.rowsFormat !== 'compact-v1' || !Array.isArray(matrix.rows)) return matrix;

  matrix.rows = matrix.rows.map((row) => expandCompactPriceRow(row, matrix));
  matrix.rowsFormat = 'expanded-v1';
  return matrix;
}

function expandCompactPriceRow(row, matrix) {
  if (!Array.isArray(row)) return row;

  const [
    shape,
    size,
    width,
    height,
    material,
    finish,
    quantity,
    total,
    unit,
    each,
    discount,
    originalTotal,
    variantIndex,
  ] = row;
  const variant = Number(variantIndex) >= 0 ? matrix.priceVariants?.[Number(variantIndex)] : null;
  const criteria = {
    shape,
    material,
    finish,
    quantity: Number(quantity || 0),
  };

  if (size) criteria.size = size;
  if (width !== '') criteria.width = Number(width);
  if (height !== '') criteria.height = Number(height);

  const price = {
    total: Number(total || 0),
    unit: Number(unit || 0),
    each: Number(each || 0),
    discount: Number(discount || 0),
    originalTotal: Number(originalTotal || total || 0),
    quantity: Number(quantity || 0),
    key: `${shape}|${size || 'Custom'}|${width || ''}|${height || ''}|${material}|${finish}|${quantity}`,
    sizeLabel: size || formatSizeLabel(Number(width || 0), Number(height || 0), true),
    material,
    finish,
    shape,
  };

  return {
    key: `compact:${price.key}`,
    criteria,
    price,
    variantTitle: variant?.title || configuredVariantLabel(price.total),
    variantId: variant?.variantId || '',
    variantGid: variant?.variantGid || '',
  };
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  }[char]));
}

if (!customElements.get('spark-product-configurator')) {
  customElements.define('spark-product-configurator', SparkProductConfigurator);
}
